/**
 * U0 · the designer, as one component.
 *
 * Mounted by the record page and by the operation-type page with one difference between
 * them: the `source` — where the envelope is read from and where writes go. A record has
 * processes, continuous operations, a bill of materials and readiness; a type has one
 * pseudo-operation and its steps. Everything else — the sequence, the loop band, the decision
 * block, the inspector, the rail — is exactly the same code, because built twice it drifts.
 *
 * RV9 · the designer — the process rail, the record, and what is blocking it.
 *
 * The ten tabs are gone. An operator does not work through "ingredients, then monitoring,
 * then sampling"; they work through one sequence, and the tabs made an author arrange that
 * sequence in ten places that could not see each other.
 *
 * Rebuilt against `reference/bmr_designer_ui` after a first attempt that was a stack of
 * generic cards with inputs in them. That version worked and read like a form, which is the
 * one thing the record must not be. What the shape buys:
 *
 *   · **the rail** — where you are in the process, always visible, with the continuous
 *     operations that run across it listed underneath rather than lost inside one operation
 *   · **a loop is a band around its body**, not an indent. D2 ruled the body *is* the
 *     children; drawing it as containment is that ruling made visible
 *   · **the inspector holds the detail**, so a step row stays one line of what it is and who
 *     does it. Every field on every card is how the first version turned into a form
 *   · **read-once sits behind a mark** (D §D4), and **the page names the record** (U-Q2)
 *
 * The client never derives. Findings, positions and delete impact all come from the server:
 * a client that renders a finding it did not receive has to invent the reason.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Drawer, Empty, Flex, Input, Modal, Segmented, Select, Space, Spin, Switch, Tooltip, Typography,
  message as antdMessage, notification as antdNotification
} from "antd";
import { InfoCircleOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { AlertTriangle, ArrowLeft, BookOpen, Boxes, Crosshair, FileText, GitBranch, Layers, Maximize2, Minimize2, PanelLeftOpen, Plus, Rocket, ShieldCheck, SlidersHorizontal, Tag, Timer } from "lucide-react";
import { addField, removeField } from "@/store/reducers/headerContent";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ReadinessProvider, useReadiness } from "@/pages/Batch-Processing/MBMRConfig/ReadinessContext";
import SpanLegend from "./components/SpanLegend";
import CopyStepDialog from "./components/CopyStepDialog";
import SpanPanel, { NEW_SPAN } from "./components/SpanPanel";
import ReadinessRail from "@/pages/Batch-Processing/MBMRConfig/ReadinessRail";
import ProcessHierarchy from "@/pages/Batch-Processing/mbmr/ProcessHierarchy";
import OperationForm from "@/pages/Batch-Processing/MBMRConfig/SubOperationDetails";
import ArchiMateFlow from "@/pages/dashboard/ArchiMateFlow";
import { createPortal } from "react-dom";
import ApprovalPanel from "@/components/ApprovalPanel";
import {
  setOperationId, setDraftOperationName, setPrevOperationId, setSelectedOperationType, setSelectedStandardBlock,
} from "@/pages/Batch-Processing/mbmr/slice/processConfig";
import { toggleRefresh } from "@/store/reducers/recipeConfigRefresh";
import {
  CoveragePanel, FieldsDialog, Finding, findingCount, findingNodes, findingTouches, InfoTip, Inserter, InspectorRail, PanelSection, PreflightQueue, SequenceView, StepForm,
  stepFacets, useVocabulary,
} from "@/designer/components";

/** F5 · what an update would do, before it is done: added, removed, and whether the record
 *  diverged from the version it was built from (then the author confirms the loss). */
function UpdateFromTypeModal({ operation, busy, preview, onCancel, onOk }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [force, setForce] = useState(false);
  useEffect(() => {
    let alive = true;
    preview().then((d) => alive && setInfo(d)).catch((e) => alive && setError(e?.response?.data?.message || "The preview could not be loaded."));
    return () => { alive = false; };
  }, [operation?.id]);
  const canGo = info && !info.up_to_date && (!info.diverged || force);
  return (
    <Modal open title={`Update ${operation?.name} to v${info?.type?.version ?? operation?.type_ref?.version}`} okText="Update"
      onCancel={onCancel} okButtonProps={{ disabled: busy || !canGo }} onOk={() => onOk(force)}>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      {!info && !error ? <Spin /> : null}
      {info ? (
        <Flex vertical gap={10} style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
            Built from v{info.built_from ?? "?"}; the type is at v{info.type.version}. The operation is re-authored from the
            current version: {info.steps_after} step{info.steps_after === 1 ? "" : "s"} after the update.
          </Text>
          {info.versions_between?.length ? (
            <div>
              <span className="dz-eyebrow">What changed in between</span>
              {info.versions_between.map((v) => <div key={v.version} className="dz-kv"><span className="k dz-code">v{v.version}</span><span className="v">{v.reason || "—"}</span></div>)}
            </div>
          ) : null}
          {info.added?.length ? <div><span className="dz-eyebrow">Added</span>{info.added.map((t) => <div key={t} style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>+ {t}</div>)}</div> : null}
          {info.removed?.length ? <div><span className="dz-eyebrow">Removed</span>{info.removed.map((t) => <div key={t} style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>− {t}</div>)}</div> : null}
          {info.diverged ? (
            <Alert type="warning" showIcon message="This record changed the operation after it was built from the type."
              description={<label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                <Switch size="small" checked={force} onChange={setForce} /> Replace those changes with the type's current version
              </label>} />
          ) : null}
          {!info.baseline_known && info.built_from ? <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>The version it was built from has no snapshot, so divergence cannot be judged.</Text> : null}
          {info.up_to_date ? <Alert type="success" showIcon message="Already at the current version." /> : null}
        </Flex>
      ) : null}
    </Modal>
  );
}

/** The In-Process Check step type, and the operation type that offers it (owner: only the 100 % visual inspection
 *  type, by its code, with its in-process sampling plan configured). The server refuses it anywhere else all the same. */
const IPC_STEP_TYPE = "IN_PROCESS_CHECK";
const IPC_OPERATION_TYPE_CODE = "OT-VI-100";
// 2026-10-29 (owner) · the step picker's "Copy step" entry — not a step type, it opens CopyStepDialog
const COPY_STEP = "__COPY_STEP__";

/** Name the type the library will carry. The code is minted from the name unless given. */
function PromoteModal({ operation, busy, onCancel, onOk }) {
  const [name, setName] = useState(operation?.name || "");
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  return (
    <Modal open title="Save as operation type" okText="Save to the library" onCancel={onCancel}
      okButtonProps={{ disabled: busy || !name.trim() }}
      onOk={() => onOk({ name: name.trim(), code: code.trim() || undefined, reason: reason.trim() })}>
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <div>
          <span className="dz-eyebrow">Name in the library</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
            placeholder="Say what it is, not which record it came from" />
        </div>
        <div>
          <span className="dz-eyebrow">Code (optional)</span>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={50}
            placeholder="Minted from the name when left blank" />
        </div>
        <div>
          <span className="dz-eyebrow">Why this type exists</span>
          <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="The version reason the library keeps" />
        </div>
        <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
          Roles travel as defaults; images stay with this record; a name already in the library is refused.
        </Text>
      </Flex>
    </Modal>
  );
}

const { Text, Title } = Typography;

/** The engine's vocabulary. Closed — a step whose type is not here is not checkable. */
/** The engine's closed vocabulary, in the shape the shared components take. */
// The vocabulary — step types, signature meanings, families, presentation limits — is served
// by the backend and read through `useVocabulary`. Nothing about it is written here.


// Stable empties. A fresh [] each render makes every memo below recompute, which on a
// 46-operation record is a visible stutter on every keystroke.
const EMPTY_LIST = [];
const EMPTY_OBJECT = {};

function Info({ children }) {
  return (
    <Tooltip title={children} overlayStyle={{ maxWidth: 380 }}>
      <InfoCircleOutlined style={{ color: "var(--dz-faint)", cursor: "help" }} />
    </Tooltip>
  );
}

/** Walk the tree once. Used for counts and for finding a node by code. */
function walk(nodes, fn, depth = 0) {
  (nodes || []).forEach((n) => {
    fn(n, depth);
    walk(n.children, fn, depth + 1);
  });
}

/**
 * The left panel is the process tree from /process-config, mounted as it is.
 *
 * It publishes its selection through the `processConfig` slice, which is what the middle and
 * right read — so there is one idea of "the operation you are looking at" rather than two
 * that drift. Writing a second tree here was the first version's mistake: it looked similar,
 * behaved differently, and would have had to be kept in step by hand forever.
 */
/**
 * Continuous operations drawn in the gutter of the process tree — beside the operations each
 * one covers, because that is literally where they are: not inside any operation, touching
 * several at once. Rails are measured off the tree's rows (`data-operation-id`), so a
 * collapsed process shows no rail and an expanded one shows the run; coverage itself comes
 * from the envelope (derived from the anchors), never worked out here.
 */
function SpanRails({ wrap, spans, operations, selectedSpan, onSelectSpan }) {
  const [rails, setRails] = useState([]);
  // A passive effect on the wrapper *element* (a callback ref hands it over), because a
  // child's layout effect runs before its parent's ref is attached — measured then, the
  // wrapper was null and the rails never appeared.
  useEffect(() => {
    if (!wrap) return undefined;
    const idByCode = Object.fromEntries((operations || []).map((o) => [o.code, String(o.id)]));
    const order = Object.fromEntries((operations || []).map((o, i) => [o.code, i]));
    const measure = () => {
      const rows = Object.fromEntries([...wrap.querySelectorAll("[data-operation-id]")]
        .map((el) => [el.getAttribute("data-operation-id"), el]));
      const base = wrap.getBoundingClientRect().top - wrap.scrollTop;
      // Lanes as the mock lays them: a rail takes the first lane free at its first operation.
      const lanes = []; const out = [];
      [...(spans || [])].sort((a, b) => (order[a.from?.operation] ?? 0) - (order[b.from?.operation] ?? 0)).forEach((sp, i) => {
        const covered = (sp.covers || []).map((c) => rows[idByCode[c]]).filter(Boolean);
        const first = order[sp.from?.operation] ?? 0, last = order[sp.to?.operation] ?? first;
        let k = 0; while (lanes[k] != null && lanes[k] >= first) k += 1;
        lanes[k] = last;
        if (!covered.length) return;
        const tops = covered.map((el) => el.getBoundingClientRect().top - base);
        const bottoms = covered.map((el) => el.getBoundingClientRect().bottom - base);
        out.push({
          id: sp.id, name: sp.name, kind: sp.span_kind, tone: (spans.indexOf(sp)) % 3, lane: k,
          top: Math.min(...tops) + 2, height: Math.max(8, Math.max(...bottoms) - Math.min(...tops) - 4)
        });
      });
      setRails(out);
    };
    measure();
    const mo = new MutationObserver(measure); mo.observe(wrap, { subtree: true, childList: true, attributes: true, attributeFilter: ["class", "style"] });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null; ro?.observe(wrap);
    return () => { mo.disconnect(); ro?.disconnect(); };
  }, [wrap, spans, operations]);
  if (!rails.length) return null;
  return (
    <div className="u0-spanrails" aria-hidden={false}>
      {rails.map((r) => (
        <button key={r.id} type="button" className={`u0-srail t${r.tone}`} aria-current={selectedSpan === r.id}
          title={`${r.name} — ${r.kind}`} style={{ top: r.top, height: r.height, left: 3 + r.lane * 6 }}
          onClick={() => onSelectSpan?.(r.id)} />
      ))}
    </div>
  );
}

/**
 * The record's stage, and the gate to the next one (U0 §4). The stages are the record's
 * approval life as the lifecycle service reports it; the gate joins nothing into a sentence —
 * it says how many findings a person must settle, and opens the list. Promotion itself
 * (D12) stays where it is: a process is sent for review from the process rail.
 */
/** Reports the lifecycle's editability to the host, which sits outside the readiness provider. */
function LifecycleProbe({ onLocked }) {
  const life = useReadiness()?.lifecycle;
  const locked = !!life && life.editable === false;
  useEffect(() => { onLocked(locked); }, [locked, onLocked]);
  // 2026-09-23 · the probe no longer renders a banner of its own. It used to show the label
  // and `editable_reason` as a two-line info banner across the top, which said what the stage
  // strip on the same screen already says — "Approved · 5 of 5 signed — read-only; raise a
  // change control to revise" — and cost two lines of the step list to repeat it. The strip
  // states the lock beside the stages it belongs to, where a person is already reading for
  // the record's state; the server's own sentence stays available there as the title, so
  // nothing the server said is lost. The probe keeps its real job: telling the host the
  // record is locked, which is what turns the editing off.
  return null;
}

/** RV9p · holds the record for editing while it is open, and reports who holds it otherwise.
 *
 *  Claims on mount (unless the lifecycle has already made the record read-only), heartbeats
 *  at the interval the server states, releases on unmount and on the page going away. A
 *  claim refused, or a heartbeat that finds the record in someone else's hands, reports the
 *  holder to the host, which turns the designer read-only and shows the banner. */
function useEditLock({ lock, enabled, onChange }) {
  const [status, setStatus] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    if (!lock || !enabled) return undefined;
    let alive = true;
    const report = (st) => { if (!alive) return; setStatus(st); onChange?.(st); };
    const beat = async () => {
      const st = await lock.heartbeat().catch(() => null);
      if (!st) return;
      if (st.edit_locked) { report(st); return; }
      // Lost but not held by anyone (a release that overtook a claim, an expiry): take it
      // back quietly rather than heartbeat into a 409 every interval.
      if (st.lost) { const again = await lock.claim().catch(() => null); if (again) report(again); }
    };
    (async () => {
      const st = await lock.claim().catch(() => null);
      if (!st) return;
      report(st);
      if (st.mine) timer.current = setInterval(beat, Math.max(5, Number(st.heartbeat_seconds) || 30) * 1000);
    })();
    const leave = () => { lock.release(); };
    window.addEventListener("pagehide", leave);
    return () => {
      alive = false;
      window.removeEventListener("pagehide", leave);
      if (timer.current) clearInterval(timer.current);
      lock.release();
    };
  }, [lock, enabled, onChange]);
  const refresh = useCallback(async () => {
    const st = await lock.claim().catch(() => null);
    if (st) { setStatus(st); onChange?.(st); }
    return st;
  }, [lock, onChange]);
  return { status, refresh };
}

/** The banner everyone but the holder sees: who has it, what they are, since when, and the
 *  take-over once their heartbeat has lapsed. Highlighted — it is why the page is read-only. */
function LockBanner({ status, lock, onTakenOver }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!status?.locked || status.mine) return null;
  const h = status.holder || {};
  const since = status.since ? new Date(status.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
  const seen = status.seconds_since_seen != null
    ? (status.seconds_since_seen < 60 ? "just now" : `${Math.round(status.seconds_since_seen / 60)} min ago`) : "";
  const who = [h.designation, h.department].filter(Boolean).join(" · ");
  const takeOver = async () => {
    setBusy(true);
    try {
      const st = await lock.takeOver(reason);
      if (st?.mine) { setAsking(false); onTakenOver?.(st); }
      else antdMessage.open({ type: "error", content: st?.message || "The record could not be taken over." });
    } finally { setBusy(false); }
  };
  return (
    <div className={`u0-lockbanner ${status.stale ? "stale" : ""}`} role="status" aria-live="polite" data-testid="lock-banner">
      <ShieldCheck size={15} />
      <div className="t">
        <b>Being edited by {h.name}</b>{who ? <span className="w"> — {who}</span> : null}
        {h.roles?.length ? <span className="r"> · {h.roles.join(", ")}</span> : null}
        <div className="m">
          Open for editing since {since}{seen ? `, last seen ${seen}` : ""}. The record is read-only for you until they leave
          {status.stale ? " — their session has gone quiet, so it may be taken over." : `; a lock may be taken over after ${status.stale_after_minutes} min without a heartbeat.`}
        </div>
      </div>
      {status.takeover_available ? (
        <Button size="small" type="primary" danger onClick={() => setAsking(true)}>Take over</Button>
      ) : null}
      <Modal open={asking} title={`Take over from ${h.name}?`} okText="Take over" okButtonProps={{ danger: true, disabled: !reason.trim(), loading: busy }}
        onOk={takeOver} onCancel={() => setAsking(false)}>
        <p style={{ marginTop: 0 }}>
          {h.name} has not been seen for {seen}. Taking over records who took the record and why against their session.
          Any edit they had not saved is theirs to redo.
        </p>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why — e.g. the author has left for the day and the correction is due tonight" />
      </Modal>
    </div>
  );
}

/** A full-screen layer for the record's DAG: full width and height, above the page. */
function DagOverlay({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`${title} — as a DAG`} data-testid="dag-overlay"
         style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", flexDirection: "column",
                  background: "var(--dz-surface, #fff)" }}>
      <div style={{ height: 52, flex: "0 0 52px", display: "flex", alignItems: "center", gap: 12, padding: "0 16px",
                    borderBottom: "1px solid var(--dz-line, #e5e7eb)" }}>
        <GitBranch size={16} />
        <b style={{ fontSize: 15, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title} — as a DAG</b>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: "var(--dz-faint, #6b7280)" }}>Esc to close</span>
        <Button size="small" onClick={onClose}>Back to the steps</Button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{children}</div>
    </div>,
    document.body,
  );
}

/** 2026-10-14 · "Approved v2" / "Draft v1.1" — the record's state beside the step's (step panel redesign). */
const recordBadgeOf = (r) => {
  if (!r || !r.state) return null;
  const st = String(r.state).replace(/_/g, " ").toLowerCase();
  return `${st[0].toUpperCase()}${st.slice(1)}${r.revision ? ` v${r.revision}` : ""}`;
};

/**
 * The pre-flight queue with EVERYTHING the stage bar counts. 2026-10-14 · the bar sums the design
 * findings, the blocked operations and the record's own findings ("2 blocking — a person must
 * settle these first"), but this drawer listed only the design findings — so "Review issues"
 * opened on "Ready · 0 blocking · Nothing is blocking this record" for v1.2, whose two blockers
 * were DEC-3 and DEC-5 with no basis. The record's findings and the blocked operations are shown
 * here too, in the same Finding shape, with where to go.
 */
function PreflightAll({ findings = [], onGoTo, title }) {
  const readiness = useReadiness() || {};
  const extra = useMemo(() => {
    const record = (readiness.model?.record_findings || []).map((f) => ({
      id: `${f.code}${f.node ? ` · ${f.node}` : ""}`,
      severity: f.severity === "blocking" ? "blocking" : "advisory",
      dimension: "Record",
      label: { text: f.label || f.code },
      why: f.why ? { text: f.why } : undefined,
      expected: f.fix ? { text: f.fix } : undefined,
      where: f.node ? [{ node: f.node, label: `${f.node}${f.operation_number ? ` · operation ${f.operation_number}` : ""}` }] : [],
    }));
    // 2026-10-29 (owner) · an instruction on every operation and step: what lacks one, per operation,
    // each step a link to it. An operation blocked only by that is not listed twice.
    const gapOf = (o) => (o.checks || []).find((c) => c.key === "instruction" && c.status === "missing");
    const opName = (o) => `Operation ${o.operation_number ?? ""} ${o.operation_name || ""}`.replace(/\s+/g, " ").trim();
    const instructions = (readiness.model?.operations || []).filter(gapOf).map((o) => {
      const gap = gapOf(o);
      return {
        id: `NO_INSTRUCTION · ${o.operation_id}`,
        severity: "blocking",
        dimension: "Instructions",
        label: { text: `${opName(o)} · ${gap.label}` },
        where: [
          ...(gap.operation_missing ? [{ operation_id: o.operation_id, label: opName(o) }] : []),
          ...(gap.missing_steps || []).map((st) => ({
            operation_id: o.operation_id, node: st.code, label: [st.code, st.title].filter(Boolean).join(" · "),
          })),
        ],
      };
    });
    const ops = (readiness.blockers || []).filter((o) => {
      const gap = gapOf(o);
      return !gap || (o.blocking_reasons || []).some((r) => r !== gap.label);
    }).map((o) => ({
      id: `OPERATION_BLOCKED · ${o.operation_id ?? o.id ?? ""}`,
      severity: "blocking",
      dimension: "Operations",
      label: { text: `Operation ${o.operation_number ?? o.number ?? ""} ${o.operation_name || o.name || ""} is blocked`.replace(/\s+/g, " ").trim() },
      where: [],
    }));
    return [...record, ...instructions, ...ops];
  }, [readiness.model, readiness.blockers]);
  return <PreflightQueue findings={[...findings, ...extra]} onGoTo={onGoTo} title={title} />;
}

// 2026-10-22 (owner) · "ye button nikal do yahan se, aur yahan pe jo blocker aata hai wo nikalna hai": the
// bar no longer carries the blocking / advisory count nor "Submit for review" (a record is sent for
// approval from the record list's Finalize). It still says why a record under review or approved is locked.
// 2026-10-28 (owner) · "View as DAG" and "View as paper" moved off this bar onto the floating rail.
// 2026-10-28 (owner) · and the bar itself is gone: a line across the page for one word. The record's
// stage is a pill beside its name in the app header; why it is read-only is that pill's tooltip.
// It is drawn here, inside the readiness provider, because the lifecycle is read from it.
function RecordStatus({ finalized = false }) {
  const dispatch = useDispatch();
  const readiness = useReadiness() || {};
  const life = readiness.lifecycle || {};
  const chain = life.chain || {};
  const labels = {
    draft: "Draft", under_review: "Under review", approved: "Approved",
    returned: "Returned for correction", partially_submitted: "Partly under review",
  };
  // A finalised record reads as approved even where the lifecycle has no chain to say so.
  const current = finalized ? "approved" : (life.state || "draft");
  const text = labels[current] || labels.draft;
  const tone = current === "approved" ? "ok" : current === "draft" || !labels[current] ? "plain" : "warn";
  const lock = current === "under_review"
    ? `Under review${chain.position ? ` · ${chain.position}` : ""}${chain.awaiting_role ? ` · awaiting ${chain.awaiting_role}` : ""} — read-only until it comes back`
    : current === "approved"
      ? `Approved${chain.position ? ` · ${chain.position} signed` : ""} — read-only; raise a change control to revise`
      : current === "returned"
        ? "Returned for correction — the reviewer's comment is on the approval chain (record facet)."
        : "";
  const note = [lock, life.editable_reason].filter(Boolean).join(" · ");
  useEffect(() => {
    dispatch(addField({ key: "_mbmrStatus", value: { text, tone, note } }));
  }, [dispatch, text, tone, note]);
  // Leaving the designer takes the pill with it — the header is shared with every other page.
  useEffect(() => () => { dispatch(removeField("_mbmrStatus")); }, [dispatch]);
  return null;
}

/** The operation's own facts, in the right pane: what it is called, who performs it, its
 *  code and instruction, and the type it was built from (fixed — the type is what the step
 *  picker offers from). The name is echoed to the process tree as it is typed, so the row on
 *  the left reads what the author is writing; it reaches the record on Save. */
// 2026-10-06 · no Save of its own: the pane's one save bar commits these with the rest of the
// operation's settings (two Save buttons on one pane read as two different saves). The form
// reports what changed through `onDirty` and is saved or discarded through its ref.
const OperationDetailsForm = forwardRef(function OperationDetailsForm({ operation, departments, busy, readOnly, autoFocus, onNameDraft, onSave, onDirty, showErrors = false }, ref) {
  // 2026-10-29 (owner) · no Instruction on the operation's form: its steps say what the operator does
  // (each step's instruction is required). An operation's existing text is left as it is.
  const from = (o) => ({ name: o?.name || "", code: o?.code || "" });
  const [draft, setDraft] = useState(() => from(operation));
  const nameRef = useRef(null);
  useEffect(() => { setDraft(from(operation)); onNameDraft?.(null); },   // eslint-disable-line react-hooks/exhaustive-deps
    [operation?.id, operation?.name, operation?.department_id, operation?.code]);
  useEffect(() => { if (autoFocus) setTimeout(() => nameRef.current?.focus({ cursor: "all" }), 60); }, [autoFocus, operation?.id]);
  const saved = from(operation);
  const dirty = Object.keys(draft).some((k) => (draft[k] ?? "") !== (saved[k] ?? ""));
  // The required fields, in the order they stand on the form.
  // 2026-10-06 · who performs the work is stated on each step ("Performed by"), not here.
  const missingList = [!draft.name.trim() && "Operation name"].filter(Boolean);
  const missing = missingList.length ? missingList.join(" and ") : null;
  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); if (k === "name") onNameDraft?.(v); };
  const changed = Object.keys(draft).filter((k) => (draft[k] ?? "") !== (saved[k] ?? ""));
  const save = async () => {
    if (!changed.length) return true;
    if (missing) return false;
    const body = {};
    changed.forEach((k) => { body[k] = draft[k] ?? null; });
    const res = await onSave(body);
    if (res?.ok) onNameDraft?.(null);
    return !!res?.ok;
  };
  useImperativeHandle(ref, () => ({ save, discard: () => { setDraft(saved); onNameDraft?.(null); }, name: () => draft.name }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onDirty?.({ count: changed.length, missing }); }, [changed.length, missing]);
  useEffect(() => () => onDirty?.({ count: 0, missing: null }), []);   // eslint-disable-line react-hooks/exhaustive-deps
  // `error` is the sentence under a required field left empty — shown after a refused Save,
  // and gone as soon as the field is filled.
  // 2026-10-14 (owner) · drawn like the step panel: label left, value right.
  const row = (label, node, required = false, error = null) => (
    <div className="u0-field">
      <span className="l">{label}{required ? <span className="u0-req" aria-label="required"> *</span> : null}</span>
      <div className="c">{node}
        {showErrors && error ? <span className="u0-fielderr" role="alert">{error}</span> : null}</div>
    </div>
  );
  return (
    <Flex vertical gap={8} className="u0-opform">
      {row("Name", <Input ref={nameRef} value={draft.name} disabled={readOnly || busy} maxLength={200}
        status={showErrors && !draft.name.trim() ? "error" : undefined}
        placeholder="What the operator knows this operation as" onChange={(e) => set("name", e.target.value)}
        onPressEnter={() => dirty && !missing && save()} />, true,
        !draft.name.trim() ? "Fill the operation name — what the operator knows this operation as." : null)}
      {row("Operation type", operation?.type_ref
        ? <span><span className="dz-chip">{operation.type_ref.code || "—"}</span> {operation.type_ref.name}</span>
        : <Text type="secondary">—</Text>)}
      {row("Code", <Input value={draft.code} disabled={readOnly || busy} maxLength={50} onChange={(e) => set("code", e.target.value)} />)}
    </Flex>
  );
});

function ProcessPanel({ hidden = false, recipeId, onLeftOpen, spans = [], operations = [], stepCounts = null, selectedSpan, onSelectSpan, addMiddle, setAddMiddle, onAddCustom, readOnly = false, onSpansChanged, onAddSpan }) {
  const [wrapEl, setWrapEl] = useState(null);
  // The tree is written to be driven by /process-config's shell, so it expects that shell's
  // callbacks — and the first version supplied them as no-ops with `fromView`, which is what
  // put the panel in read-only mode. It is the same tree an author edits on
  // /process-config; giving it less here would mean two panels that look the same and do
  // different things.
  //
  // So these are the shell's real callbacks, not stubs: adding an operation dispatches the
  // same actions to `processConfig` that /process-config dispatches, and the designer reads
  // its selection from that slice. One store, one idea of what is selected.
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isAddOperation, setIsAddOperation] = useState(false);
  // The add-operation chooser's open state is the host's, so the middle pane can open the
  // same chooser for a process that has no operation yet (the tree offers Add Operation
  // only on an existing operation's menu, so an empty process had no way in).
  const noop = useCallback(() => { }, []);
  // The tree raises `isAddOperation` on a plain operation click too — on /process-config that
  // is what shows the operation form in its right pane. Here a click selects the operation and
  // the middle pane shows its steps, so the tree may only CLOSE the dialog; opening it is
  // reserved for the Add Operation chooser (`handleSubmitAddOperation` below).
  const closeFromTree = useCallback((v) => { if (!v) setIsAddOperation(false); }, []);

  const handleSubmitAddOperation = useCallback(
    ({ type, block, operation_type, previous_operation_id, copy_from_operation_id }) => {
      // A custom operation is made at once from its type and opened in the designer — its
      // facts in the right pane, its steps in the middle — rather than in the ten-panel form.
      if (type === "custom" && operation_type && onAddCustom) {
        setAddMiddle?.(false);
        onAddCustom({ operation_type_id: operation_type, after: previous_operation_id || null });
        return;
      }
      // 2026-10-07 · the Operation Library too: the block becomes an operation with its steps,
      // here in the designer, not in the ten-panel form (operation_authoring.add_from_block).
      if (type === "standard" && block && onAddCustom) {
        setAddMiddle?.(false);
        // The picker's value is the block's operation type (as on /process-config).
        onAddCustom({ standard_block_type_id: block, after: previous_operation_id || null });
        return;
      }
      // 2026-10-28 · Copy Operation: a Process Library operation, copied with its steps
      // (operation_authoring.add_copy). It used to open an empty ten-panel form and copy nothing.
      if (type === "copy" && copy_from_operation_id && onAddCustom) {
        setAddMiddle?.(false);
        onAddCustom({ copy_from_operation_id, after: previous_operation_id || null });
        return;
      }
      if (type === "standard" && block) {
        dispatch(setSelectedStandardBlock(block));
        dispatch(setSelectedOperationType(null));
      } else if (type === "custom") {
        dispatch(setSelectedOperationType(operation_type));
        dispatch(setSelectedStandardBlock(null));
      } else {
        dispatch(setSelectedOperationType(null));
        dispatch(setSelectedStandardBlock(null));
      }
      dispatch(setPrevOperationId(previous_operation_id || null));
      setIsAddOperation(true);
    },
    [dispatch, onAddCustom, setAddMiddle]
  );

  return (
    <div className="dz-col dz-rail dz-processpanel" style={hidden ? { display: "none" } : undefined}>
      {/* 2026-09-28 · the operation the tree just asked for.
          The rail offers Add Operation and Edit Operation — it is the same tree
          /process-config renders — and publishes the choice through the `processConfig`
          slice. /process-config then shows the operation form in its right pane; here the
          middle pane belongs to the STEPS, so nothing rendered the form and Submit closed
          the dialog and visibly did nothing. It opens over the designer instead, which
          leaves the steps view exactly as it was. The form is the same component, so every
          validation it carries — required type, name and department, the sub-operation
          stage rules, the machine-setup gate, the fields locked while a process is with its
          approvers — applies here unchanged rather than being restated. */}
      <Modal
        open={isAddOperation}
        onCancel={() => setIsAddOperation(false)}
        footer={null}
        width="min(1180px, 94vw)"
        destroyOnHidden
        styles={{ body: { maxHeight: "78vh", overflowY: "auto", paddingTop: 8 } }}
        title="Operation"
      >
        <OperationForm
          is_add_middle_modal={addMiddle}
          set_is_add_middle_modal={setAddMiddle}
          set_isAddOperation={setIsAddOperation}
          isAddOperation={isAddOperation}
          isShowDag={false}
          set_isShowDag={noop}
        />
      </Modal>
      <div className="u0-treewrap" ref={setWrapEl}>
        <SpanRails wrap={wrapEl} spans={spans} operations={operations} selectedSpan={selectedSpan} onSelectSpan={onSelectSpan} />
        <ProcessHierarchy
          loading={loading}
          setLoading={setLoading}
          setProcessLength={noop}
          // The tree's own ‹ collapses the panel on /process-config through this callback.
          // Wired to a no-op here, the button did nothing — the same panel, the same button,
          // and a different result, which is the drift U0 exists to prevent. It drives the
          // same state as the header icon; either form the tree passes is honoured.
          setIsLeftDrawerOpen={(v) => onLeftOpen?.(typeof v === "function" ? v(true) : !!v)}
          handleSubmitAddOperation={handleSubmitAddOperation}
          is_add_middle_modal={addMiddle}
          set_is_add_middle_modal={setAddMiddle}
          isAddOperation={isAddOperation}
          set_isAddOperation={closeFromTree}
          onApprovalStatusChange={noop}
          locked={readOnly}
          operationStepCounts={stepCounts}
        />
      </div>
      {/* Continuous work, named beside the sequence rather than in it — always shown, with an
          Add on an editable record (2026-10-14, owner). */}
      <SpanLegend spans={spans} operations={operations} selectedSpan={selectedSpan}
                  onSelectSpan={onSelectSpan} readOnly={readOnly} onAdd={onAddSpan} />
    </div>
  );
}

/**
 * The inspector carries the detail, which is what keeps a step row to one line of what it is
 * and who does it. Every field on every card is exactly how the first version became a form.
 *
 * It answers three questions, in the order an author asks them: what is blocking this, what
 * is this step, and what runs across it. The findings come first because a record that cannot
 * be issued is the thing you needed to know before editing anything.
 */
function Inspector({ processInfo, operation, node, findings, counts, spans, recipe, version, busy,
  roles, onGoTo, onEdit, onDelete, dag, facet, onFacets, onPickFacet, span, onClearSpan, spanPanel = null,
  vocab = {}, STEP_TYPE_OPTIONS = [], SIGN_MEANINGS = [], SIGN_LABELS = [], LABEL = {}, checklists = [],
  rules = [], onBind, onTighten, onUnbind, onRefresh, aqlSetup = null,
  derived = {}, nodesByCode = {}, onConcurrency,
  isRecord = true, showRole = true, blastRadius = null, extraDetails = null,
  onOpenMfr, onBomFromMfr, onRefreshRules, onHandover, onLotStep, onReconciliation, onDependsOn, onArea, onDispensing, areaList = [], onPromote, onUpdateFromType, files = null, readOnly = false,
  masters = { parameters: [], labels: [], units: [], categories: [] }, fieldOps = null, checklistOps = null, onCreateParameter = null,
  onEditOperation = null, departments = [], onNameDraft = null, focusOperation = null, onStepDraft = null,
  equipmentAt = {}, clearanceProfiles = [] }) {
  // The readiness roll-up /process-config shows in its right pane — the same component, the
  // same provider, as a facet here. `blockers` is its own count of blocked operations.
  const readiness = useReadiness() || {};
  const blockers = readiness.blockers || [];
  // The provenance block links to the record a copy came from; the pane had no navigate of
  // its own, so opening the record facet of a copied record threw a ReferenceError.
  const navigate = useNavigate();
  // The operation's own settings collect in a draft and reach the record on Save, the same
  // way a step's fields do — a person sets area, stations and handover together, then commits.
  const [opDraft, setOpDraft] = useState({});
  // The kinds of material this record's BOM holds (served as `bom_roles`). A dispensing
  // operation with no lane yet starts with every one of them selected, as an unsaved draft
  // the author confirms with Save — the lane is the BOM's materials unless narrowed. Other
  // operations start empty: a lane on a blending step would ask the floor to weigh again.
  // 2026-10-07 · no lane pre-filled from the BOM: Dispenses is not on the pane any more, and a
  // draft the author cannot see would read "1 change not saved" for nothing.
  useEffect(() => { setOpDraft({}); }, [operation?.id]);
  // The operation's own facts (name, department, code, instruction) are drafted in their form
  // and saved by the same bar as the settings below them.
  const detailsRef = useRef(null);
  const [detailsDirty, setDetailsDirty] = useState({ count: 0, missing: null });
  // Set by a Save refused for an empty required field: those fields are drawn red and the bar
  // names them. Each clears as it is filled — the red and the message follow what is missing.
  const [opTried, setOpTried] = useState(false);
  useEffect(() => { setOpTried(false); }, [operation?.id]);
  // A missing name is said in the bar at once (a new operation starts unnamed); the field itself
  // turns red when a Save is refused.
  const opBlocked = detailsDirty.missing
    ? `Fill the required field${detailsDirty.missing.includes(" and ") ? "s" : ""}: ${detailsDirty.missing}` : null;
  const opChanges = Object.keys(opDraft).length + detailsDirty.count;
  const opDirty = opChanges > 0;
  // CR-09 · the operation's cleanroom GRADE is no longer edited here — that control moved to
  // the step, where the work moves, and carries forward from there. `onArea` and its endpoint
  // stay: the operation's own grade is what C-12 derives `aseptic` from, and the paper path
  // still writes it. Kept as a read below rather than removed.
  //
  // The dispensing lane is a different fact and stays on the operation, correctly: a GRADE is
  // where a step happens, a PERMITTED AREA is which booths this lane may be worked in, and one
  // operation offers several for the floor to choose between.
  //
  // spec/21 section 5 — `roles` says WHICH materials this operation dispenses and is the thing
  // that defines a lane, so the number of lanes a record has is configuration. `areas` is the
  // PERMITTED set; the floor picks one at execution.
  const shown_roles = "roles" in opDraft ? opDraft.roles : (operation?.dispensing?.roles || []);
  const shown_areas = "area_ids" in opDraft
    ? opDraft.area_ids : ((operation?.dispensing?.areas || []).map((a) => a.id));
  // The booths the record permits, authored beside the areas. Not the equipment standing in
  // the area: that suite holds a glove box, an RLAF and six balances, and a balance is not a
  // booth — which is why this is a decision the record makes rather than a list it infers.
  const shown_booths = "booth_ids" in opDraft
    ? opDraft.booth_ids : ((operation?.dispensing?.booths || []).map((b) => b.id));
  const shown_handover = "handover" in opDraft ? opDraft.handover : (operation?.handover || { required: false, text: "" });
  // FR-07 · the step of a filling operation that makes its lots, and which of its fields means what. Drafted with the
  // operation's other settings and sent on Save; the server checks it (types, required fields, one field one meaning).
  const lotSaved = operation?.lot_step ? { step: operation.lot_step.node, fields: operation.lot_step.fields || {} } : { step: "", fields: {} };
  const shown_lot = "lot_step" in opDraft ? opDraft.lot_step : lotSaved;
  const reconSaved = operation?.reconciliation ? { step: operation.reconciliation.step || "", batch_yield: operation.reconciliation.batch_yield || {}, theoretical_yield: operation.reconciliation.theoretical_yield || {} } : { step: "", batch_yield: {}, theoretical_yield: {} };
  const shown_recon = "reconciliation" in opDraft ? opDraft.reconciliation : reconSaved;
  const signSteps = [];
  const collectSignSteps = (nodes) => (nodes || []).forEach((n) => { if (n.kind === "STEP" && String(n.step_type || "").toUpperCase() === "SIGN") signSteps.push(n); collectSignSteps(n.children); });
  const isViOperation = operation?.inspection?.behaviour === "UNIT_COUNT";
  if (onReconciliation && isViOperation) collectSignSteps(operation.children);
  const lotSteps = [];
  const collectLotSteps = (nodes) => (nodes || []).forEach((n) => { if (n.kind === "STEP" && (n.fields || []).length) lotSteps.push(n); collectLotSteps(n.children); });
  if (onLotStep && operation?.makes_lots) collectLotSteps(operation.children);
  // What this operation waits for — the record's order, edited as the old process-config
  // dependency picker did. Only whole-operation (BATCH) waits are picked here; a per-tub
  // (SUBJECT) wait says more than a list can and is shown, not edited.
  const savedDeps = (operation?.depends_on || []).filter((d) => d.scope !== "SUBJECT").map((d) => d.operation_id);
  const shown_deps = "depends_on" in opDraft ? opDraft.depends_on : savedDeps;
  const askReason = (names) => new Promise((resolve) => {
    let reason = "";
    Modal.confirm({
      title: "Why does it no longer wait?",
      content: (
        <Flex vertical gap={6}>
          <Text style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>Removing: {names}. The record keeps who withdrew the dependency and why.</Text>
          <Input.TextArea autoFocus rows={2} placeholder="e.g. Runs in parallel per SOP-MFG-012"
            onChange={(e) => { reason = e.target.value; }} />
        </Flex>
      ),
      okText: "Remove dependency",
      onOk: () => (reason.trim() ? resolve(reason.trim()) : Promise.reject(antdMessage.warning("Give a reason."))),
      onCancel: () => resolve(null),
    });
  });
  const saveOp = async () => {
    if (detailsDirty.missing) { setOpTried(true); return; }
    if (detailsRef.current && !(await detailsRef.current.save())) return;
    setOpTried(false);
    // Each part clears on its own success; a refused write leaves its part in the draft.
    const left = { ...opDraft };
    // `apply` answers with the response whether or not it went through; `ok` is the success.
    if ("grade" in opDraft && onArea && (await onArea(operation.id, opDraft.grade))?.ok) delete left.grade;
    // Areas and booths are authored on the step now, so this panel stages only the lane's
    // roles. They are still SENT — unchanged — because `onDispensing` writes the lane whole and
    // omitting them would clear what the record already says.
    if ("roles" in opDraft && onDispensing
      && (await onDispensing(operation.id, {
        roles: shown_roles, area_ids: shown_areas, booth_ids: shown_booths }))?.ok) {
      delete left.roles;
    }
    if ("stations" in opDraft && onConcurrency && (await onConcurrency(operation.id, opDraft.stations))?.ok) delete left.stations;
    if ("handover" in opDraft && onHandover && (await onHandover(operation.id, opDraft.handover))?.ok) delete left.handover;
    if ("lot_step" in opDraft && onLotStep && (await onLotStep(operation.id, opDraft.lot_step))?.ok) delete left.lot_step;
    if ("reconciliation" in opDraft && onReconciliation && (await onReconciliation(operation.id, { step: opDraft.reconciliation.step, limits: { batch_yield: opDraft.reconciliation.batch_yield, theoretical_yield: opDraft.reconciliation.theoretical_yield } }))?.ok) delete left.reconciliation;
    if ("depends_on" in opDraft && onDependsOn) {
      const removed = savedDeps.filter((id) => !opDraft.depends_on.includes(id));
      let reason = "";
      if (removed.length) {
        const byId = Object.fromEntries((operation.depends_on || []).map((d) => [d.operation_id, d]));
        reason = await askReason(removed.map((id) => `#${byId[id]?.number} ${byId[id]?.name || ""}`.trim()).join(", "));
      }
      if (!removed.length || reason) {
        if ((await onDependsOn(operation.id, { operation_ids: opDraft.depends_on, reason }))?.ok) delete left.depends_on;
      }
    }
    setOpDraft(left);
    // 2026-10-14 (owner) · said once every part of it went through; a refused part keeps its own
    // error and leaves its part staged, so no success is claimed for it.
    if (!Object.keys(left).length) {
      const name = String(detailsRef.current?.name?.() ?? operation?.name ?? "").trim() || `Operation ${operation?.number ?? ""}`.trim();
      antdMessage.success(`“${name}” saved successfully.`);
    }
  };
  const forNode = findings.filter((f) => findingTouches(f, node?.code));
  const forOp = findings.filter((f) =>
    (f.where || []).some((w) => w.operation === operation?.code)
  );
  const shown = node ? forNode : forOp.length ? forOp : findings;

  // Spans that cross this operation. A continuous operation belongs to no single step, which
  // is why it is listed here rather than pushed into one of them.
  const crossing = (spans || []).filter(
    (s) => s.from?.operation === operation?.code || s.to?.operation === operation?.code
  );

  const refs = recipe?.references || {};
  const quality = refs.quality || { cqas: [] };
  const uncontrolled = (quality.cqas || []).filter((q) => !(q.controlled_by || []).length).length;
  const mfr = refs.mfr || null;
  const bom = refs.bom || {};
  const bomLines = bom.lines || [];
  const worst = shown.some((f) => f.severity === "blocking") ? "block" : "warn";

  // The inspector holds four different kinds of fact about four different things, and stacked
  // down one column the record's own details sat below however many findings it had. Each is
  // a facet now; the rail carries the counts so nothing is hidden by being closed.
  // One rail. When a step is selected its facets ARE the details — the form's own facets
  // merge in here rather than the form carrying a second rail inside this one.
  const own = span
    ? [{ key: "span", icon: Timer, label: `Continuous operation — ${span.name}`, tone: "plain" }]
    : node
      ? stepFacets(node, { showRole: true })
      : [{ key: "details", icon: Layers, label: isRecord ? (operation ? "The operation" : "The record") : "The operation type" }];
  const stepKeys = new Set(own.map((f) => f.key));
  const facets = [
    ...own,
    {
      key: "blocking", icon: AlertTriangle,
      label: node ? "What is blocking this step" : isRecord ? "What is blocking this record" : "What is blocking this type",
      badge: shown.length || null, tone: worst
    },
    // Record-level facts. A type has no bill of materials, no change control and no
    // readiness roll-up — the facets are simply absent rather than empty.
    isRecord && (bomLines.length || bom.note)
      ? {
        key: "bom", icon: Boxes, label: "Bill of materials",
        badge: bomLines.length || true, tone: bomLines.length ? "plain" : "warn"
      }
      : null,
    isRecord ? { key: "record", icon: Tag, label: "The record — version and change control" } : null,
    // §49 · what controls quality: the product's CQAs, the steps claimed to control each
    // (CPPs) and the material attributes that move it (CMAs). One uncontrolled CQA is an
    // unevidenced claim in the specification, which is why the count sits on the rail.
    isRecord ? {
      key: "quality", icon: Crosshair,
      label: uncontrolled ? `What controls quality — ${uncontrolled} attribute${uncontrolled === 1 ? "" : "s"} uncontrolled` : "What controls quality",
      badge: uncontrolled || null, tone: uncontrolled ? "warn" : "plain"
    } : null,
    // The master formula record the record was derived from: the document an author keeps
    // open while designing, so it is one click on the rail and never further.
    isRecord && mfr ? {
      key: "mfr", icon: BookOpen, label: `Master formula record — ${mfr.code}${mfr.version ? ` v${mfr.version}` : ""}`,
      badge: mfr.status && mfr.status !== "APPROVED" ? mfr.status : null,
      tone: mfr.status && mfr.status !== "APPROVED" ? "warn" : "plain"
    } : null,
    isRecord ? {
      key: "readiness", icon: ShieldCheck,
      label: blockers.length ? `Readiness — ${blockers.length} operation${blockers.length === 1 ? "" : "s"} blocking submission`
        : "Readiness — the record's roll-up",
      badge: blockers.length || null, tone: blockers.length ? "block" : "plain"
    } : null,
  ].filter(Boolean);

  const home = span ? "span" : node ? "step" : "details";
  const at = facets.some((f) => f.key === facet) ? facet : home;
  const inStep = stepKeys.has(at);
  const backLabel = span ? "Back to the continuous operation" : node ? "Back to the step"
    : !isRecord ? "Back to the operation type" : operation ? "Back to the operation" : "Back to the record";
  // The rail component keeps its own open/closed state in the provider and collapses itself
  // on narrow screens. As a facet it is open by definition while it is the facet shown.
  useEffect(() => { if (at === "readiness") readiness.setRailOpen?.(true); }, [at]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The host owns the rail; it needs these facets to draw it. Reported whenever they change.
  const facetsKey = JSON.stringify(facets.map((f) => [f.key, f.label, !!f.badge, f.tone]));
  useEffect(() => { onFacets?.(facets, at); }, [facetsKey, at]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 2026-10-14 (owner) · on an editable step or operation there is no "Step" / "Operation" heading
  // row: its remove button and the blocking / clear pill stand at the start of the Save bar.
  const headActions = (<>
          {node ? (
            // Removing a step is the one irreversible thing here. Small and out of the way of
            // the fields; what it will take with it is shown before anything happens.
            <Tooltip title={`Remove ${node.code}${node.title ? ` · ${node.title}` : ""}`}>
              <Button size="small" type="text" danger icon={<MinusCircleOutlined />}
                disabled={busy || readOnly} aria-label="Remove step"
                onClick={() => onDelete(node.code)} />
            </Tooltip>
          ) : null}
          {/* A count of what is wrong is the one thing a reader reaches for, and these were
              plain spans: the number was shown and the findings behind it were three clicks
              away on the rail, with nothing saying so. They open the facet that lists them.
              Reading is not writing — these stay live on a record under review, where knowing
              what blocks it is exactly what a reviewer is there to do. */}
          {/* 2026-10-28 (owner) · a toggle: pressed again, it goes back to what the pane showed. */}
          {counts.blocking ? (
            <button type="button" className="dz-pill block" onClick={() => onPickFacet?.(at === "blocking" ? home : "blocking")}
              aria-pressed={at === "blocking"}
              title={at === "blocking" ? backLabel : "Show what is blocking this"}>{counts.blocking} blocking</button>
          ) : null}
          {counts.advisory ? (
            <button type="button" className="dz-pill warn" onClick={() => onPickFacet?.(at === "blocking" ? home : "blocking")}
              aria-pressed={at === "blocking"}
              title={at === "blocking" ? backLabel : "Show the advisory findings"}>{counts.advisory} advisory</button>
          ) : null}
          {!counts.blocking && !counts.advisory ? (
            <span className="dz-pill ok">clear</span>
          ) : null}
</>);
  const opBar = !readOnly && !!operation && (onEditOperation || onArea || onConcurrency || onHandover || onLotStep || onReconciliation || onDependsOn);
  const headInBar = !readOnly && ((inStep && !!node) || (!node && at === "details" && !!opBar));
  return (
    // u0-ro: a record that cannot be edited here reads as a record (grey fields, no arrows, no editing hints) — surface.css
    <div className={`dz-col${readOnly ? " u0-ro" : ""}`}>
      {headInBar ? null : <div className="dz-head">
        <span className="dz-eyebrow">
          {node ? "Step" : !isRecord ? "Operation type" : operation ? `Operation ${operation.number ?? ""}` : "Record"}
        </span>
        <Space size={6}>{headActions}        </Space>
      </div>}

      <div className="dz-insp u0-insp-split">
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>

          {at === "blocking" ? (<>
            {/* 2026-10-28 (owner) · the way back to the step's own panel, where the list is read. */}
            <Button type="link" size="small" icon={<ArrowLeft size={14} />} onClick={() => onPickFacet?.(home)}
              style={{ alignSelf: "flex-start", paddingInline: 0 }}>{backLabel}</Button>
            {shown.length ? (
              <Flex vertical gap={8}>
                <span className="dz-eyebrow">
                  {node ? "Blocking this step" : "Blocking this record"}
                </span>
                {/* D12 · one component, three places. Every word is the check's own — a surface
                  that explained a finding would be inventing a reason. */}
                {/* "Open the step" (fix action `focus_step`) was printed as text and did nothing:
                    it opens the step the finding names now, the same as its Where link. */}
                {shown.map((f) => (
                  <Finding key={f.id} finding={f} placement="panel" onGoTo={onGoTo} whereLimit={vocab.limits?.finding_where}
                    onFix={f.fix?.action === "focus_step"
                      ? () => onGoTo?.((f.where || []).find((w) => w.node) || (node ? { node: node.code } : null))
                      : undefined} />
                ))}
              </Flex>
            ) : (
              <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
                {node ? "Nothing is blocking this step." : "Nothing is blocking this record."}
              </Text>
            )}
          </>) : null}

          {at === "bom" ? (
            <Flex vertical gap={8}>
              <span className="dz-eyebrow">Bill of materials</span>
              {bom.summary?.length ? (
                <Flex vertical gap={4}>
                  {bom.summary.map((d) => (
                    <div key={d.label} className="dz-kv">
                      <span className="k">{d.label}</span>
                      <span className="v">{d.value}</span>
                    </div>
                  ))}
                </Flex>
              ) : null}
              {bomLines.length ? (
                <div className="u0-bom">
                  {bomLines.map((l, i) => (
                    <div key={`${l.sequence}-${i}`} className="u0-bomline">
                      <div className="h">
                        <span className="seq">{l.sequence}</span>
                        <span className="m">{l.material || "— unnamed material"}</span>
                        {l.role ? <span className="dz-chip">{l.role}</span> : null}
                      </div>
                      {(l.cma || []).length ? (
                        <div className="s">{l.cma.map((a, i) => <span key={i} className="u0-cma" title={a.spec}>CMA · {a.attribute} → {(a.moves || []).join(", ")}</span>)}</div>
                      ) : null}
                      <div className="q">
                        {/* A quantity-sufficient line has no fixed amount. A blank where a
                          number goes would read as missing data rather than as the fact. */}
                        <span className="v">{l.qs ? "quantity sufficient" : l.quantity || "—"}</span>
                        {l.dispense && l.dispense !== l.quantity ? (
                          <span className="d">dispense {l.dispense}</span>
                        ) : null}
                        {l.overage ? <span className="d">overage {l.overage}</span> : null}
                        {l.tolerance ? <span className="d">{l.tolerance}</span> : null}
                      </div>
                      {l.stage ? <div className="s">{l.stage}</div> : null}
                      {l.sap_code ? <div className="s dz-code">{l.sap_code}</div> : null}
                    </div>
                  ))}
                </div>
              ) : (
                <Flex vertical gap={6} align="flex-start">
                  <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>{bom.note}</Text>
                  {/* A record made since 2026-09-28 opens with no bill: bring it from the MFR.
                      The server evaluates the MFR at the batch size and writes no process. */}
                  {onBomFromMfr && !readOnly ? (
                    <Button size="small" type="primary" onClick={() => onBomFromMfr({})}>
                      Bring the bill of materials from the MFR
                    </Button>
                  ) : null}
                </Flex>
              )}
            </Flex>
          ) : null}

          {at === "span" && span && spanPanel ? (
            <SpanPanel span={span} {...spanPanel} />
          ) : at === "span" && span ? (
            <Flex vertical gap={10} className="u0-spaninsp">
              <div className="u0-stepid" data-family="structure">
                <div className="u0-stepid-title" style={{ cursor: "default" }}>{span.name}</div>
                <div className="u0-stepid-row">
                  <span className="kind">{span.span_kind}</span>
                  <span className="dz-code">{span.id}</span>
                  {span.cadence ? <span>{span.cadence}</span> : null}
                </div>
              </div>
              <Flex vertical gap={4}>
                <span className="u0-eyebrow">It is not inside an operation</span>
                <div className="dz-kv"><span className="k">starts</span>
                  <span className="v"><span className="dz-code">{span.from?.operation}</span> at {span.from?.at}</span></div>
                <div className="dz-kv"><span className="k">ends</span>
                  <span className="v"><span className="dz-code">{span.to?.operation}</span> at {span.to?.at}</span></div>
                <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                  Anchored to two points in the sequence. Which operations it covers is <b>derived</b> from
                  those anchors — nobody maintains a list, so inserting an operation between them puts it
                  under the monitor automatically, and moving one out is a finding rather than a silence.
                </Text>
              </Flex>
              <Flex vertical gap={4}>
                <span className="u0-eyebrow">Covers {span.covers?.length || 0} operations — derived</span>
                <div className="u0-covmap">
                  {(span.covers || []).map((code, i, arr) => (
                    <button key={code} type="button" className={`covrow ${code === operation?.code ? "here" : ""}`}
                      onClick={() => onGoTo?.({ operation: code })}>
                      <span className={`brk ${arr.length === 1 ? "only" : i === 0 ? "top" : i === arr.length - 1 ? "bot" : "mid"}`} />
                      <span className="cvname">{code}</span>
                      {i === 0 ? <span className="anch">starts</span> : null}
                      {i === arr.length - 1 ? <span className="anch">ends</span> : null}
                      {code === operation?.code ? <span className="hn">you are here</span> : null}
                    </button>
                  ))}
                </div>
              </Flex>
              <Flex vertical gap={4}>
                {[["Monitors", span.monitors], ["Records", span.records], ["Who", span.role],
                ["On breach", span.on_breach?.effect ? `${span.on_breach.effect} · ${span.on_breach.scope || ""}` : null]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="dz-kv"><span className="k">{k}</span><span className="v">{v}</span></div>
                  ))}
              </Flex>
              {(span.children || []).length ? (
                <Flex vertical gap={4}>
                  <span className="u0-eyebrow">Steps it owns · {span.children.length}</span>
                  <SequenceView compact vocab={vocab} steps={span.children} actions={false} />
                </Flex>
              ) : null}
              <Button size="small" onClick={onClearSpan}>Back to the operation</Button>
            </Flex>
          ) : null}

          {at === "readiness" ? (
            <div className="u0-readiness">
              <ReadinessRail />
            </div>
          ) : null}

          {at === "record" ? (
            <Flex vertical gap={8}>
              <span className="dz-eyebrow">Record</span>
              <Flex vertical gap={4}>
                <div className="dz-kv"><span className="k">State</span>
                  <span className="v">{recipe?.state}</span></div>
                <div className="dz-kv"><span className="k">Revision</span>
                  <span className="v">{recipe?.revision}</span></div>
                <div className="dz-kv">
                  <span className="k">
                    <Space size={4}>Version <Info>
                      Not the revision. This moves on every edit, and is how two authors editing
                      at once are kept from overwriting each other.
                    </Info></Space>
                  </span>
                  <span className="v">{version}</span>
                </div>
                {(refs.record || []).map((d) => (
                  <div key={d.label} className="dz-kv">
                    <span className="k">{d.label}</span>
                    <span className="v">{d.value}</span>
                  </div>
                ))}
              </Flex>
              {isRecord && recipe?.provenance ? (
                /* RV9t · where the record came from and who has had their hands on it: the
                   source it was copied from, who prepared it, who changed it last, who approved.
                   Read from the envelope, never composed here. */
                <Flex vertical gap={4} data-testid="provenance">
                  {onRefreshRules && !readOnly && recipe.provenance ? (
                    <Flex gap={8} align="center" wrap>
                      <span className="dz-eyebrow">Timed rules and the plant policy</span>
                      <Tooltip title="Every timed rule on this record that draws its numbers from a plant policy (eye rest, and any rule whose limits_from names a policy) is set to the policy's current values. Rules the record authored itself are untouched.">
                        <Button size="small" data-testid="refresh-rules" onClick={async () => { const res = await onRefreshRules(); const r = res?.policy; if (r) antdMessage.open({ type: "success", content: `${r.updated} of ${r.checked} policy-sourced rule${r.checked === 1 ? "" : "s"} updated from the plant policy.` }); }}>
                          Update rules from plant policy
                        </Button>
                      </Tooltip>
                    </Flex>
                  ) : null}
                  <span className="dz-eyebrow">Provenance — where it came from, who prepared and approved it</span>
                  <div className="u0-prov">
                    {recipe.provenance.copied_from ? (
                      <div className="dz-kv"><span className="k">Copied from</span>
                        <span className="v"><a href={`/record-designer/${recipe.provenance.copied_from.id}`} onClick={(e) => { e.preventDefault(); navigate(`/record-designer/${recipe.provenance.copied_from.id}`); }}>
                          {recipe.provenance.copied_from.code} · {recipe.provenance.copied_from.name} v{recipe.provenance.copied_from.revision}</a>
                          {recipe.provenance.copied_by ? ` — by ${recipe.provenance.copied_by.name}${recipe.provenance.copied_on ? ` on ${new Date(recipe.provenance.copied_on).toLocaleDateString()}` : ""}` : ""}</span></div>
                    ) : (
                      <div className="dz-kv"><span className="k">Origin</span><span className="v">Authored here{recipe.provenance.root ? ` · version ${recipe.provenance.version_number} of ${recipe.provenance.root.code}` : ""}</span></div>
                    )}
                    <div className="dz-kv"><span className="k">Prepared by</span>
                      <span className="v">{recipe.provenance.prepared_by ? `${recipe.provenance.prepared_by.name}${recipe.provenance.prepared_by.designation ? ` · ${recipe.provenance.prepared_by.designation}` : ""}` : "—"}
                        {recipe.provenance.prepared_on ? ` · ${new Date(recipe.provenance.prepared_on).toLocaleDateString()}` : ""}</span></div>
                    <div className="dz-kv"><span className="k">Last changed by</span>
                      <span className="v">{recipe.provenance.last_modified_by ? recipe.provenance.last_modified_by.name : "—"}
                        {recipe.provenance.last_modified_on ? ` · ${new Date(recipe.provenance.last_modified_on).toLocaleString()}` : ""}</span></div>
                    {(recipe.provenance.approved_by || []).length ? recipe.provenance.approved_by.map((a, i) => (
                      <div key={i} className="dz-kv"><span className="k">{i === 0 ? "Approved by" : ""}</span>
                        <span className="v">{a.name}{a.role ? ` · ${a.role}` : a.designation ? ` · ${a.designation}` : ""} — {a.meaning}{a.on ? ` · ${new Date(a.on).toLocaleString()}` : ""}</span></div>
                    )) : <div className="dz-kv"><span className="k">Approved by</span><span className="v">not yet approved</span></div>}
                  </div>
                </Flex>
              ) : null}
              {isRecord && recipe?.id ? (
                /* The approval chain — who has signed, who is next, and the act itself for a
                   reviewer who holds the role. The same panel every record type uses. */
                <Flex vertical gap={4}>
                  <span className="dz-eyebrow">Approval — the chain this record moves through</span>
                  <div className="u0-approval">
                    <ApprovalPanel objectType="Recipe" objectId={Number(recipe.id)} compact onChanged={() => readiness.reload?.()} />
                  </div>
                </Flex>
              ) : null}
            </Flex>
          ) : null}

          {at === "quality" ? (
            <Flex vertical gap={8}>
              <span className="dz-eyebrow">What controls quality</span>
              {quality.cqas?.length ? quality.cqas.map((q) => (
                <div key={q.code} className={`u0-cqarow ${(q.controlled_by || []).length ? "" : "bad"}`}>
                  <div className="h"><span className="dz-code">{q.code}</span><b>{q.name}</b></div>
                  <div className="s">{q.specification || <i>no specification recorded</i>}{q.source_ref ? <span className="src"> · {q.source_ref}</span> : null}</div>
                  <div className="c">
                    <span className="k">Controlled by</span>
                    {(q.controlled_by || []).length
                      ? q.controlled_by.map((s) => (
                        <button key={s.code} type="button" className="u0-cqastep" title={s.title} onClick={() => onGoTo?.({ node: s.code })}>{s.code}</button>
                      ))
                      : <span className="miss">nothing in this record controls it</span>}
                  </div>
                  {(q.cmas || []).length ? (
                    <div className="c"><span className="k">Material attributes</span>
                      {q.cmas.map((m, i) => <span key={i} className="dz-chip" title={m.spec}>{m.material} · {m.attribute}</span>)}</div>
                  ) : null}
                </div>
              )) : <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>{quality.note || "No quality attributes are recorded for this product."}</Text>}
              <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                A CQA is a property of the product that must hold. A CPP is a step whose parameter moves it —
                ticked on the step. A CMA is a material attribute that moves it from the input side — on the bill of
                materials. The edges are the point: they are what lets a reviewer ask what affects assay.
              </Text>
            </Flex>
          ) : null}

          {at === "mfr" && mfr ? (
            <Flex vertical gap={8}>
              <span className="dz-eyebrow">Master formula record</span>
              <div className="u0-stepid" data-family="record">
                <div className="u0-stepid-title" style={{ cursor: "default" }}>{mfr.code}</div>
                <div className="u0-stepid-row">
                  <span className="dz-code">{mfr.kind}{mfr.version ? ` · v${mfr.version}` : ""}</span>
                  <span className={`dz-pill ${mfr.status === "APPROVED" ? "ok" : "warn"}`}>{mfr.status || "—"}</span>
                </div>
              </div>
              <Flex vertical gap={4}>
                {[["Product", mfr.product], ["Formula basis", mfr.basis], ["Effective from", mfr.effective_date],
                ["Change control", mfr.change_control], ["Source document", mfr.source_document],
                ["Approved by", mfr.approved_by ? `${mfr.approved_by}${mfr.approved_at ? ` · ${mfr.approved_at.slice(0, 10)}` : ""}` : ""]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="dz-kv"><span className="k">{k}</span><span className="v">{v}</span></div>
                  ))}
              </Flex>
              <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                The record is derived from this MFR: quantities, limits and the process narrative
                come from it. A change to the MFR is a new version, and this record says which it
                was built from.
              </Text>
              {(mfr.cpps || []).length ? (
                <Flex vertical gap={4}>
                  <span className="dz-eyebrow">Critical process parameters on the MFR</span>
                  {mfr.cpps.map((p, i) => (
                    <div key={i} className="dz-kv"><span className="k">{p.stage ? `${p.stage} · ` : ""}{p.name}</span>
                      <span className="v">{[p.min != null ? `min ${p.min}` : null, p.target != null ? `target ${p.target}` : null, p.max != null ? `max ${p.max}` : null].filter(Boolean).join(" · ")} {p.uom}</span></div>
                  ))}
                </Flex>
              ) : null}
              <Button size="small" onClick={() => onOpenMfr?.(mfr)}>Open the master formula records</Button>
            </Flex>
          ) : null}

          {inStep && node ? (
            /* U0 §2 · the SAME form the library edits a sub-operation template with. The
               differences are inputs, not code: the designer names a role and has no blast
               radius, because a step change reaches one recipe. `facet` is controlled — the
               rail is this panel's, not the form's. */
            <StepForm
              recordBadge={recordBadgeOf(recipe)}
              headActions={headInBar ? headActions : null}
              step={{ ...node, step_type_label: LABEL[node.step_type] }}
              readOnly={readOnly}
              vocab={vocab}
              masters={masters}
              fieldOps={fieldOps}
              checklists={checklists}
              clearanceProfiles={clearanceProfiles}
              checklistOps={checklistOps}
              onCreateParameter={onCreateParameter}
              stepTypes={STEP_TYPE_OPTIONS}
              signMeanings={SIGN_MEANINGS}
              signLabels={SIGN_LABELS}
              roles={roles}
              showRole={showRole}
              blastRadius={blastRadius}
              facet={at}
              // CR-10 preview: a SEQUENCE rule names another operation of this record.
              operations={(recipe?.operations || []).map((o) => ({ code: o.code, name: o.name }))}
              operationCode={operation?.code || null}
              // CR-09 · where this step is performed, derived by the server with its source.
              area={derived[`step:${node.code}#area`] || null}
              // What this step may charge: a line this record carries, never a free list.
              bomLines={bomLines}
              // The operation's lane — which KINDS of material it dispenses. Authored on the
              // operation, so narrowing the step's picker by it narrows by something a person
              // stated, not by a guess off the step's type or its name.
              laneRoles={operation?.dispensing?.roles || []}
              // 2026-09-24 · where the work may happen, authored on the step beside its grade.
              // 2026-09-28 · `boothList` is gone: the equipment was flattened here so a
              // second dropdown could narrow it to the chosen areas, and there is no second
              // dropdown any more — the step form builds one cascade straight from
              // `areaList`, which already carries each area's booths nested inside it.
              areaList={areaList}
              // A repeated step takes its material from the collection, one per iteration —
              // naming one here would pin every pass to the same material. The server already
              // derives loop membership (`node:<code>#membership`); reading the tree's shape
              // here would be the client deriving it a second time (RV0 §A3, §A4).
              inLoop={!!derived[`node:${node.code}#membership`]?.value}
              operation={operation}
              derived={derived}
              nodesByCode={nodesByCode}
              onEditNode={onEdit}
              onConcurrency={(n) => onConcurrency?.(operation?.id, n)}
              onChange={(patch) => onEdit(node.code, patch)}
              onDraft={onStepDraft}
              carriedEquipment={(node && equipmentAt?.[node.code]) || null}
              files={files}
              cqas={refs.quality?.cqas || []}
              rules={rules}
              aqlSetup={aqlSetup}
              onBind={onBind ? (body) => onBind(node.code, body) : undefined}
              onTighten={onTighten ? (id, limits) => onTighten(node.code, id, limits) : undefined}
              onUnbind={onUnbind ? (id) => onUnbind(node.code, id) : undefined}
              onRefresh={onRefresh ? (id) => onRefresh(node.code, id) : undefined}
            />
          ) : null}

          {at === "details" ? (
            <>
              {operation ? (
                <>
                  {/* Hidden, not merely disabled, while the record cannot be written: a bar that
                    says "All changes saved" beside a dead Save button offers an action that does
                    not exist here, and the reason it does not is already stated in the banner
                    above. Nothing is saved on a record under review, so nothing claims to be. */}

                  {/* 2026-10-14 (owner) · the operation panel drawn like the step panel: a header
                     (kind · record state · ⓘ), flat sections, explanations behind ⓘ. */}
                  <div className="u0-stepform u0-p2 u0-opp">
                  {/* 2026-10-28 (owner) · the Save line pinned at the top of the pane while it scrolls. */}
                  {!readOnly && (onEditOperation || onArea || onConcurrency || onHandover || onLotStep || onReconciliation || onDependsOn) ? (
                    <div className={`u0-savebar ${opDirty ? "on" : ""}`} role="status" aria-live="polite">
                      <span className={`u0-savebar-note ${opBlocked ? "blocked" : ""}`}>
                        {opBlocked || (opDirty ? `${opChanges} change${opChanges === 1 ? "" : "s"} not saved` : "All changes saved")}</span>
                      <Space size={6}>
                        {headInBar ? headActions : null}
                        <Button size="small" disabled={!opDirty || busy || readOnly}
                                onClick={() => { setOpDraft({}); setOpTried(false); detailsRef.current?.discard(); }}>Discard</Button>
                        <Button size="small" type="primary" disabled={!opDirty || busy || readOnly} onClick={saveOp}>Save</Button>
                      </Space>
                    </div>
                  ) : null}
                  <div className="u0-stepid">
                    <div className="u0-stephead">
                      <span className="u0-typeword">Operation</span>
                      <span className={`u0-statebadge ${readOnly ? "ro" : "rw"}`}>
                        {[recordBadgeOf(recipe), readOnly ? "read-only" : "editable"].filter(Boolean).join(" · ")}
                      </span>
                      <span style={{ flex: 1 }} />
                      <InfoTip label="About this operation" text={`Code ${operation.code || "—"}${operation.id ? ` · id ${operation.id}` : ""}. Select a step in the middle to edit it.`} />
                    </div>
                    {/* 2026-10-28 (owner) · no "Operation 2 · <process>" line under the name: the left
                        panel already places the operation in its process. */}
                    <div className="u0-stepid-title u0-opp-title">{operation.name || "Untitled operation"}</div>
                  </div>
                  <PanelSection title="Details" info="The name the operator knows this operation as, the type it was made from, and its code.">
                  <Flex vertical gap={6}>
                    {onEditOperation ? (
                      <OperationDetailsForm operation={operation} departments={departments} busy={busy} readOnly={readOnly}
                        autoFocus={focusOperation === operation.id} onNameDraft={onNameDraft}
                        ref={detailsRef} onDirty={setDetailsDirty} showErrors={opTried}
                        onSave={(body) => onEditOperation(operation.id, body)} />
                    ) : (
                      <>
                        <Title level={5} style={{ margin: 0 }}>{operation.name}</Title>
                        <div className="dz-kv"><span className="k">Code</span>
                          <span className="v dz-code">{operation.code}</span></div>
                      </>
                    )}
                    {/* 2026-09-28 · no Area row here. Area is authored on the step (CR-09), and
                      the operation-level read only said "— set on the steps" for most. */}
                    {/* M13's stations, in the wire's one shape. Set here because concurrency
                      belongs to the operation, not to any step inside it. */}
                    {/* 2026-10-06 · Process, Phase, Materials and Runs on are not shown here, and
                       (2026-10-07) neither is Dispenses: the author does not set them on this pane.
                       A lane the record already states is kept as it is. */}
                  </Flex>
                  </PanelSection>
                  {onDependsOn ? (
                    /* The order: which operations must be finished before this one may start.
                       The runner enforces it (services/order/gate.py), and the legacy screens
                       read the same answer from the operation's dependency column. */
                    <PanelSection title="Runs after" info="Which operations must be finished before this one may start. The runner enforces it.">
                    <Flex vertical gap={6}>
                      <Select mode="multiple" size="small" allowClear showSearch optionFilterProp="label"
                        disabled={busy || readOnly} value={shown_deps} style={{ width: "100%" }}
                        placeholder="No dependency — may start as soon as the batch does"
                        // 2026-10-14 (owner) · only the operations BEFORE this one may be chosen; one
                        // already chosen stays listed so it never shows as a bare id.
                        options={Object.values((recipe?.operations || []).filter((o) => o.id !== operation.id
                          && ((operation.number == null || o.number == null || o.number < operation.number)
                              || (shown_deps || []).includes(o.id)))
                          .reduce((acc, o) => {
                            const k = o.process_name || "Operations";
                            (acc[k] = acc[k] || { label: k, options: [] }).options.push(
                              { value: o.id, label: `#${o.number} · ${o.name}` });
                            return acc;
                          }, {}))}
                        onChange={(ids) => setOpDraft((d) => ({ ...d, depends_on: ids || [] }))} />
                      {(operation.depends_on || []).filter((d) => d.scope === "SUBJECT").map((d) => (
                        <Text key={d.edge_id} type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                          Also waits for #{d.number} {d.name} per {d.subject} — set in the order view.
                        </Text>
                      ))}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {onHandover ? (
                    /* What a newcomer signs before their first action on this operation in a
                       batch — operator or supervisor, a replacement mid-run. Per operation and
                       per person; the record's general instructions are the once-per-batch
                       counterpart on the record facet. */
                    <PanelSection title="Handover" info="What a newcomer — operator or supervisor, a replacement mid-run — reads and e-signs before their first action on this operation in a batch.">
                    <Flex vertical gap={6}>
                      <Flex align="center" gap={8}>
                        <Switch size="small" checked={!!shown_handover.required} disabled={busy || readOnly}
                          onChange={(on) => setOpDraft((d) => ({ ...d, handover: { required: on, text: shown_handover.text || "" } }))} />
                        <Text style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
                          {shown_handover.required
                            ? "Required — every person reads and e-signs this before their first action here"
                            : "Not required on this operation"}
                        </Text>
                      </Flex>
                      <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: vocab.limits?.instruction_rows }}
                        key={`ho-${operation.id}`} defaultValue={operation.handover?.text || ""} disabled={busy || readOnly}
                        placeholder="What a person taking over must know: the lot in progress, the log so far, the check due next"
                        onChange={(e) => e.target.value !== (operation.handover?.text || "")
                          && setOpDraft((d) => ({ ...d, handover: { required: !!shown_handover.required, text: e.target.value } }))} />
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {onReconciliation && isViOperation ? (
                    /* BMR 19.10.1 · the reconciliation after visual inspection is signed on one Sign step of this operation, and each of its
                       two yields has a permitted range. The figures come from the records; nothing is typed here but the step and the ranges. */
                    <PanelSection title="Reconciliation" info="The Sign step on which QA signs the reconciliation after visual inspection, and the permitted range of each yield. The figures themselves are read from the inspection records.">
                    <Flex vertical gap={6}>
                      <Select size="small" allowClear placeholder="— the Sign step for the reconciliation —" disabled={busy || readOnly || !signSteps.length}
                        value={shown_recon.step || undefined} options={signSteps.map((n) => ({ value: n.code, label: `${n.code} · ${n.title || n.label || "step"}` }))}
                        onChange={(v) => setOpDraft((x) => ({ ...x, reconciliation: { ...shown_recon, step: v || "" } }))} />
                      {!signSteps.length && !readOnly ? <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>Add a Sign step to this operation first.</Text> : null}
                      {[["batch_yield", "Batch yield after visual inspection"], ["theoretical_yield", "Theoretical yield after visual inspection"]].map(([key, word]) => (
                        <Flex key={key} align="center" gap={8}>
                          <Text style={{ flex: 1, fontSize: "calc(12px * var(--dz-fs, 1))" }}>{word} (%)</Text>
                          {["min", "max"].map((end) => (
                            <Input key={end} size="small" style={{ width: 80 }} placeholder={end} disabled={busy || readOnly} inputMode="decimal"
                              value={(shown_recon[key] || {})[end] ?? ""}
                              onChange={(e) => setOpDraft((x) => ({ ...x, reconciliation: { ...shown_recon, [key]: { ...(shown_recon[key] || {}), [end]: e.target.value } } }))} />
                          ))}
                        </Flex>
                      ))}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {onLotStep && operation.makes_lots ? (
                    /* FR-07 · a filling operation makes its lots from one of its own steps: the filling operator records the lot
                       there (number, tubs, times) and the lot and its tubs exist for inspection the moment it is signed. This
                       says which step, and which of its fields means what. */
                    <PanelSection title="Lot creation" info="Which step of this operation makes a filling lot when it is recorded, and which of that step's fields is the lot number, the tubs and the times. Without it a lot is never made from the step.">
                    <Flex vertical gap={6}>
                      <Flex align="center" gap={8}>
                        <Switch size="small" checked={!!shown_lot.step} disabled={busy || readOnly || !lotSteps.length}
                          onChange={(on) => setOpDraft((d) => ({ ...d, lot_step: on ? { step: lotSteps[0]?.code || "", fields: {} } : { step: "", fields: {} } }))} />
                        <Text style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
                          {shown_lot.step ? "A step of this operation makes the lots" : lotSteps.length || readOnly ? "No step makes lots" : "Give a step its fields first (the lot number, the tubs, the times)"}
                        </Text>
                      </Flex>
                      {shown_lot.step ? (() => {
                        const step = lotSteps.find((n) => n.code === shown_lot.step);
                        const labels = (type) => (step?.fields || []).filter((f) => f.data_type === type).map((f) => ({ value: f.label, label: f.label }));
                        const ROLES = [["lot_no", "Lot number", true, "INTEGER"], ["tub_from", "First tub", true, "INTEGER"], ["tub_to", "Last tub", true, "INTEGER"],
                          ["units", "Units in the lot", false, "INTEGER"], ["filling_start", "Filling start", false, "DATE"], ["filling_end", "Filling end", false, "DATE"],
                          ["cold_room_in", "Cold-room transfer", false, "DATE"]];
                        const used = Object.values(shown_lot.fields || {}).filter(Boolean);
                        const missing = ROLES.filter(([k, , req]) => req && !(shown_lot.fields || {})[k]).map(([, l]) => l);
                        const twice = [...new Set(used.filter((v, i) => used.indexOf(v) !== i))];
                        return (
                          <>
                            <Select size="small" value={shown_lot.step} disabled={busy || readOnly}
                              options={lotSteps.map((n) => ({ value: n.code, label: `${n.code} · ${n.title || n.label || "step"}` }))}
                              onChange={(code) => setOpDraft((d) => ({ ...d, lot_step: { step: code, fields: {} } }))} />
                            {ROLES.map(([key, word, req, type]) => (
                              <Flex key={key} align="center" gap={8}>
                                <Text style={{ width: 150, fontSize: "calc(12px * var(--dz-fs, 1))" }}>{word}{req ? <span style={{ color: "#b3261e" }}> *</span> : <Text type="secondary"> (optional)</Text>}</Text>
                                <Select size="small" style={{ flex: 1 }} allowClear={!req} placeholder="— choose the field —" disabled={busy || readOnly}
                                  value={(shown_lot.fields || {})[key] || undefined} options={labels(type)}
                                  onChange={(v) => setOpDraft((d) => ({ ...d, lot_step: { step: shown_lot.step, fields: { ...(shown_lot.fields || {}), [key]: v || "" } } }))} />
                              </Flex>
                            ))}
                            {missing.length ? <Text type="danger" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>Still to choose: {missing.join(", ")}</Text> : null}
                            {twice.length ? <Text type="danger" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>One field cannot mean two things: {twice.join(", ")}</Text> : null}
                          </>
                        );
                      })() : null}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {operation.type_ref?.has_template ? (
                    /* F5 · which type and which state of it this operation copied, and the
                       way to the current version when one exists. */
                    <PanelSection title="Built from" info="Which library type, and which version of it, this operation was copied from.">
                    <Flex vertical gap={4}>
                      <div className="dz-kv"><span className="k dz-code">{operation.type_ref.code}</span>
                        <span className="v">{operation.type_ref.name}
                          {operation.type_ref.built_from ? ` · v${operation.type_ref.built_from}` : " · version not stamped"}
                          {operation.type_ref.built_from && operation.type_ref.built_from < operation.type_ref.version
                            ? <span className="dz-pill warn" style={{ marginLeft: 6 }}>v{operation.type_ref.version} available</span>
                            : operation.type_ref.built_from ? <span className="dz-pill ok" style={{ marginLeft: 6 }}>current</span> : null}
                        </span></div>
                      {operation.type_ref.equipment_class ? (
                        <div className="dz-kv"><span className="k">Equipment class</span><span className="v">{operation.type_ref.equipment_class}</span></div>
                      ) : null}
                      {onUpdateFromType && operation.type_ref.built_from && operation.type_ref.built_from < operation.type_ref.version ? (
                        <div>
                          <Button size="small" disabled={busy || readOnly} onClick={() => onUpdateFromType(operation)}>
                            Update to v{operation.type_ref.version}
                          </Button>
                        </div>
                      ) : null}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {onPromote ? (
                    /* The operation, as authored, becomes a library type — loops, decisions,
                       stations, handover and all — so the next record starts from it. */
                    <PanelSection title="Library" info="What is authored here — its repeats, decisions, stations and handover — becomes a type other records are built from. This record keeps its own steps.">
                    <Flex vertical gap={4}>
                      <div>
                        <Button size="small" icon={<Boxes size={13} />} disabled={busy || readOnly} onClick={() => onPromote(operation)}>
                          Save as operation type
                        </Button>
                      </div>
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {(operation.equipment || []).length ? (
                    <PanelSection title="Equipment" info="From the record's list of equipment.">
                    <Flex vertical gap={4}>
                      {operation.equipment.map((m) => <div key={m} className="dz-kv"><span className="v" style={{ textAlign: "left" }}>{m}</span></div>)}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {(operation.required_attachments || []).length ? (
                    <PanelSection title="Attachments" info="The attachments the batch must carry at this operation.">
                    <Flex vertical gap={4}>
                      {operation.required_attachments.map((a) => (
                        <div key={a.code} className="dz-kv"><span className="k dz-code">{a.code}</span>
                          <span className="v">{a.label}{a.when !== "ALWAYS" ? ` · ${a.when.toLowerCase()}${a.trigger ? ` on ${a.trigger}` : ""}` : ""}</span></div>
                      ))}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  {operation.inspection ? (
                    <PanelSection title="Inspection" info="The data sets this operation works with.">
                    <Flex vertical gap={4}>
                      {operation.inspection.work_unit ? <div className="dz-kv"><span className="k">Work unit</span><span className="v">{operation.inspection.work_unit}</span></div> : null}
                      {operation.inspection.stages?.length ? <div className="dz-kv"><span className="k">Stages</span><span className="v">{operation.inspection.stages.join(" → ")}</span></div> : null}
                      {operation.inspection.defect_set ? (
                        <details className="u0-details">
                          <summary><span className="k">Defect set</span> <b>{operation.inspection.defect_set.name}</b> · {operation.inspection.defect_set.codes.length} codes</summary>
                          <div className="u0-codes">
                            {operation.inspection.defect_set.codes.map((c) => (
                              <div key={c.code} className="dz-kv"><span className="k dz-code">{c.code}</span><span className="v">{c.name}<span className="dz-chip" style={{ marginLeft: 6 }}>{c.criticality?.toLowerCase()}</span></span></div>
                            ))}
                          </div>
                        </details>
                      ) : null}
                      {operation.inspection.aql ? <div className="dz-kv"><span className="k">AQL plan</span><span className="v">{operation.inspection.aql.plan} · level {operation.inspection.aql.level} · critical {operation.inspection.aql.critical} · major {operation.inspection.aql.major} · minor {operation.inspection.aql.minor}</span></div> : null}
                      {operation.inspection.ipc_sampling ? (
                        <details className="u0-details">
                          <summary><span className="k">In-process sampling</span> <b>{operation.inspection.ipc_sampling.bands.length} lot-size bands</b> · {operation.inspection.ipc_sampling.accumulate_min_units}–{operation.inspection.ipc_sampling.accumulate_max_units} units</summary>
                          <div className="u0-codes">
                            <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>{operation.inspection.ipc_sampling.plan}</Text>
                            <table className="u0-plan"><thead><tr><th>Lot size</th><th>Sample</th>{operation.inspection.ipc_sampling.bands[0].levels.map((l) => <th key={l.level}>{l.level}</th>)}</tr></thead>
                              <tbody>{operation.inspection.ipc_sampling.bands.map((b) => (
                                <tr key={b.lot_min}><td>{b.lot_min}–{b.lot_max}</td><td>{b.sample}</td>{b.levels.map((l) => <td key={l.level}>{l.accept == null ? "#" : l.accept} / {l.reject}</td>)}</tr>
                              ))}</tbody></table>
                            <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>accept / reject per sampling level · # acceptance not permitted at this sample size</Text>
                          </div>
                        </details>
                      ) : null}
                      {operation.inspection.ipc_sampling ? (() => {
                        // Which step is the operation's In-Process Check (added from the step picker), while that step exists.
                        const find = (nodes, code) => (nodes || []).reduce((hit, n) => hit || (n.code === code ? n : find(n.children, code)), null);
                        const ipcNode = operation.ipc_step?.node ? find(operation.children, operation.ipc_step.node) : null;
                        return <div className="dz-kv"><span className="k">In-Process Check step</span><span className="v">{ipcNode ? `${ipcNode.code} · ${ipcNode.title || "In-Process Check"}` : "not added yet — add it from the step picker"}</span></div>;
                      })() : null}
                      {operation.inspection.ipc ? <div className="dz-kv"><span className="k">In-process checks</span><span className="v">{Object.entries(operation.inspection.ipc).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}</span></div> : null}
                    </Flex>
                    </PanelSection>
                  ) : null}
                  </div>
                  {extraDetails}
                </>
              ) : processInfo ? (
                /* 2026-09-28 · a process is selected but no operation. The inspector used to
                   fall through to record readiness here, which answers a different question
                   — and before this the first operation was selected silently anyway, so the
                   state could not be reached. What a process HAS is what it holds: its
                   operations, and the steps under them. */
                <>
                  <span className="dz-eyebrow">Process</span>
                  <div className="u0-stepid">
                    <div className="u0-stepid-title" style={{ cursor: "default" }}>
                      {processInfo.name || "Untitled process"}
                    </div>
                    <div className="u0-stepid-row">
                      <span className="dz-chip">
                        {processInfo.operations.length} operation{processInfo.operations.length === 1 ? "" : "s"}
                      </span>
                      <span className="dz-chip">
                        {processInfo.steps} step{processInfo.steps === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <div className="u0-facts">
                    {processInfo.operations.map((o, i) => (
                      <div className="u0-kv" key={o.id}>
                        <span className="k">{i + 1}</span>
                        <span className="v">{o.name || o.code}</span>
                      </div>
                    ))}
                  </div>
                  <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
                    Pick an operation to see its steps and edit it.
                  </Text>
                </>
              ) : (
                <CoveragePanel
                  title="Record readiness"
                  blocking={counts.blocking}
                  advisory={counts.advisory}
                  rows={(findings || []).map((f) => ({
                    key: f.id,
                    count: findingCount(f),
                    label: f.label?.text,
                    severity: f.severity,
                    why: f.why?.text,
                  }))}
                />
              )}

              {crossing.length ? (
                <Flex vertical gap={6}>
                  <span className="dz-eyebrow">Runs across this operation</span>
                  {crossing.map((sp) => (
                    // A span is not a finding, so it does not borrow a finding's styling. It is
                    // a thing that runs across operations, and the bracket says how far.
                    <div key={sp.id} className="u0-span">
                      <div className="n">{sp.name}</div>
                      <div className="r">
                        <span className="a">{sp.from?.operation}</span>
                        <span className="bar" />
                        <span className="a">{sp.to?.operation}</span>
                      </div>
                      <div className="k">
                        {sp.span_kind}{sp.cadence ? ` · ${sp.cadence}` : ""}
                      </div>
                    </div>
                  ))}
                </Flex>
              ) : null}
            </>
          ) : null}
        </div>

      </div>
    </div>
  );
}

// MbomLine.role, as the backend closes it. Used only as a fallback when the vocabulary
// endpoint has not been extended yet; the server remains the authority.
const MATERIAL_ROLES = [
  { value: "API", label: "Active ingredient" },
  { value: "EXCIPIENT", label: "Excipient" },
  { value: "PH_ADJUST", label: "pH adjuster" },
  { value: "SOLVENT", label: "Solvent / vehicle" },
  { value: "GAS", label: "Process gas" },
  { value: "PROCESS_AID", label: "Processing aid" },
  { value: "PRIMARY_PM", label: "Primary packaging" },
  { value: "SECONDARY_PM", label: "Secondary packaging" },
  { value: "OTHER", label: "Other" },
];

export default function Designer({ source }) {
  // `source` says where the envelope comes from and where writes go. `id` and `kind` are
  // the two facts the host reads; every verb is called on it and never composed here.
  const recipeId = source.id;
  const isRecord = source.kind === "record";
  // The process rail belongs to any subject that HAS processes and operations. A record
  // has many; a Process Library block has exactly one — that is what makes it a block —
  // and its operations are the whole point of opening it here. Only an operation type has
  // none (it IS one operation), so that is the one kind the rail stays off for.
  const hasProcessRail = isRecord || source.kind === "library";
  // The app does not mount antd's <App> provider, so the hook's message/modal/notification
  // would be silent no-ops; the static instances render their own holders and always show.
  const modal = Modal, notification = antdNotification;
  // The static message API differs between antd builds (some expose only `open`); one
  // shape here, so a refusal is never lost to a missing helper.
  const message = useMemo(() => {
    const say = (type) => (content) => (typeof antdMessage.open === "function"
      ? antdMessage.open({ type, content }) : console.warn(`[designer] ${type}: ${content}`));   // eslint-disable-line no-console
    return { error: say("error"), warning: say("warning"), success: say("success"), info: say("info") };
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const vocab = useVocabulary(source.getVocabulary);
  const STEP_TYPE_OPTIONS = useMemo(() => (vocab.step_types || []).map(({ value, label }) => ({ value, label })), [vocab]);
  // Steps from the closed vocabulary, and the structures that are inserted at a point: a
  // decision, and a repeat.
  //
  // A repeat used to be wrappable only — select a run of steps, then Repeat (D10). That asks
  // an author to write the body before they can say what it is a body OF, which is backwards
  // for the case it is mostly used for: "dispense each excipient" is a decision about the
  // record, taken before any dispensing step exists. Both ways now work — the repeat is
  // inserted empty and its body written inside it, and the pick-bar still wraps an existing
  // run — because an author who has already written the steps should not have to delete them.
  const INSERTER_SOURCE = useMemo(() => [
    ...(vocab.step_types || []).map(({ value, label, group, code, hint }) =>
      ({ value, label, family: group, code, hint })),
    ...(vocab.structures || []).filter((t) =>
      (t.value === "DECISION" && source.insertDecision)
      || (t.value === "LOOP" && source.insertStep)).map(({ value, label, group, code, hint }) =>
      ({ value, label, family: group, code, hint })),
  ], [vocab, source]);
  const SIGN_MEANINGS = useMemo(() => vocab.sign_meanings || [], [vocab]);
  const SIGN_LABELS = useMemo(() => vocab.sign_labels || [], [vocab]);
  const LABEL = useMemo(() => Object.fromEntries((vocab.step_types || []).map((t) => [t.value, t.label])), [vocab]);
  const [busy, setBusy] = useState(false);
  const [promoting, setPromoting] = useState(null);
  const [updating, setUpdating] = useState(null);
  // A type opens read-only: it is master data, and most visits are to read it. Edit is a
  // deliberate step, and everything that writes is withheld until it is taken.
  const [editing, setEditing] = useState(!source.startReadOnly);
  // A record under review or approved is read-only for everyone: the lifecycle says so and
  // the server refuses a write that slips past. The type page keeps its own View/Edit.
  // Reported by a probe inside the readiness provider (this component sits outside it).
  const [lifecycleLocked, setLifecycleLocked] = useState(false);
  // RV9p: someone else holds the record for editing. Reported by the lock hook; the server
  // refuses a write from anyone but the holder, so this only spares the author the refusal.
  const [heldByOther, setHeldByOther] = useState(false);
  const onLockChange = useCallback((st) => { setHeldByOther(!!st?.locked && !st.mine); }, []);
  // An approved record is the one batches are issued from, so it is never editable. Read off
  // the record itself (Recipe_Status), not only the lifecycle: a record finalised through the
  // older flow has no approval chain, and the lifecycle would call it a draft. It also holds
  // from the first paint, before readiness has answered.
  const recipeState = state?.authored?.recipe?.state;
  const finalized = isRecord && (recipeState === "APPROVED" || recipeState === "ARCHIVED");
  const editLock = useEditLock({ lock: source.lock, enabled: isRecord && !lifecycleLocked && !finalized, onChange: onLockChange });
  // 2026-10-29 (owner) · `viewOnly`: opened to read (a standard block's eye icon) — every write withheld, no Edit switch
  const readOnly = !!source.viewOnly || (!!source.startReadOnly && !editing) || lifecycleLocked || heldByOther || finalized;
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("List");
  // 2026-10-28 (owner) · the Design / Validate switch is gone (not used): the sequence is always
  // drawn in Design. Fixed, not read from "dz.mode" — a viewer last left in Validate would
  // otherwise stay there with no switch to leave it.
  const designMode = "design";
  // The area master, read once per mount. The lane pickers need the same list on every
  // operation, and the record cannot state a permitted area the plant does not have.
  const [areaList, setAreaList] = useState([]);
  useEffect(() => {
    let live = true;
    if (!source.listAreas) return undefined;
    source.listAreas()
      .then((rows) => { if (live) setAreaList(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (live) setAreaList([]); });
    return () => { live = false; };
  }, [source]);
  // The whole record as a DAG, in the middle pane. View state, like the List | Flow toggle.
  const [dag, setDag] = useState(false);
  // The one rail, owned here so it outlives the inspector: full view hides the inspector, and
  // a rail that vanished with it would take the way back out with it. The inspector reports
  // its facets; the host adds the record-level handles — DAG, layout, readiness.
  const [facet, setFacet] = useState("details");
  const [inspFacets, setInspFacets] = useState({ items: [], at: "details" });
  const onFacets = useCallback((items, at) => setInspFacets({ items, at }), []);
  // The operation the shared tree has selected. One source of truth for "where am I".
  const selectedOperationId = useSelector((st) => st?.processConfig?.operation_id);
  // Which PROCESS the rail has open, so the inspector can answer for it while no operation
  // is chosen. The rail clears `operation_id` whenever the process changes, so the two are
  // never both in force.
  const selectedProcessId = useSelector((st) => st?.processConfig?.process_id);
  const selectedProcessName = useSelector((st) => st?.processConfig?.process);
  // Who performs an operation — the department master, for the operation form.
  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    let alive = true;
    source.listDepartments?.().then((rows) => alive && setDepartments(rows)).catch(() => { });
    return () => { alive = false; };
  }, [source]);
  // An operation just made from Add Operation → Custom: selected once the tree has reloaded
  // (its reload clears the selection), and its name field focused.
  const pendingSelect = useRef(null);
  const [focusOperation, setFocusOperation] = useState(null);
  // The add-operation chooser (`OperationButton` inside the tree). Held here rather than in
  // ProcessPanel so the empty middle pane can open it for a process with no operations.
  const [addMiddle, setAddMiddle] = useState(false);
  // The tree dispatches `toggleRefresh()` whenever it changes the process or its operations.
  // Without following it, an operation added on the left would not appear in the middle
  // until a manual reload — two panels over one record, disagreeing.
  const treeRefresh = useSelector((st) => st?.recipeconfigrefresh?.refresh);
  const [roles, setRoles] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [masters, setMasters] = useState({ parameters: [], labels: [], units: [], categories: [] });
  const [rules, setRules] = useState([]);
  const [clearanceProfiles, setClearanceProfiles] = useState([]);
  const [preflight, setPreflight] = useState(false);
  // Double-click opens the step full width. The mode changes the default, not the access —
  // the step is still selectable in the list, and this is the way to see all of it at once.
  const [stepOpen, setStepOpen] = useState(false);
  // The page itself does not scroll on the designer: the three panes (and the step drawer)
  // each scroll on their own, between the app header and footer, with the bars hidden.
  useEffect(() => {
    // From the top: the record's header (stage) stays in view, fixed, above
    // the panes — a page left scrolled would hide it with no way back.
    window.scrollTo(0, 0);
    document.querySelectorAll(".app-content-scroll").forEach((el) => { el.scrollTop = 0; });
    document.documentElement.classList.add("dz-scroll-lock");
    return () => document.documentElement.classList.remove("dz-scroll-lock");
  }, []);
  const [placing, setPlacing] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [why, setWhy] = useState("");

  useEffect(() => {
    if (!placing) { setProposal(null); setWhy(""); return; }
    let alive = true;
    source.getProposedOrder(placing.id)
      .then((p) => alive && setProposal(p))
      .catch(() => alive && message.error("Could not read what is unplaced here."));
    return () => { alive = false; };
  }, [placing, message, source]);
  // Set in the effect body, not only at ref creation. React's dev StrictMode runs the mount
  // effect, its cleanup, then the effect again; a cleanup-only version flipped the ref to
  // false forever, every guarded setState was skipped, and the record stayed on its spinner
  // after every hot reload.
  const mounted = useRef(true);
  // Where the frame starts on the page, measured once: the details pane is fixed to the
  // viewport from that line down, so it floats with the rail instead of scrolling away.
  const frameRef = useRef(null);
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    // Viewport-relative, with NO `+ window.scrollY`. The inspector is `position: fixed`, which
    // is measured from the viewport, and the columns now scroll inside themselves rather than
    // with the page — so the document offset that used to be right is now a gap the size of
    // however far the page happens to be scrolled. All three columns start on one line.
    el.style.setProperty("--dz-top", `${Math.round(el.getBoundingClientRect().top)}px`);
  });

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Frame layout. The left tree collapses to a strip; the middle can take the whole frame.
  // Both are view state — nothing about the record changes — and both survive a reload
  // as a per-viewer convenience only.
  const [leftOpen, setLeftOpen] = useState(() => {
    try { return localStorage.getItem("dz.leftOpen") !== "0"; } catch { return true; }
  });
  const [wide, setWide] = useState(false);
  // 2026-10-28 (owner) · the viewer's "Show / hide fields" popup (FieldsDialog), from the floating toolbar.
  const [fieldsOpen, setFieldsOpen] = useState(false);
  // The details pane collapses on its own, like the tree. The rail stays — it is fixed to
  // the viewport — so a facet is always one click from reopening the pane.
  const [rightOpen, setRightOpen] = useState(true);
  // A continuous operation, selected from the legend or a band. It is not inside an
  // operation, so selecting it is not selecting a step; it has its own facet.
  const [selectedSpan, setSelectedSpan] = useState(null);
  useEffect(() => { try { localStorage.setItem("dz.leftOpen", leftOpen ? "1" : "0"); } catch { } },
    [leftOpen]);

  // The shared process tree titles itself "{Product} - {Record}" from two store fields the
  // recipe shell sets. This shell hosts the same tree, so it sets the same two facts — read
  // off the envelope, not typed here.
  const authoredRecipe = state?.authored?.recipe;
  useEffect(() => {
    if (!authoredRecipe || !isRecord) return;
    dispatch(addField({ key: "Product", value: authoredRecipe.product || "" }));
    dispatch(addField({ key: "MBMR", value: authoredRecipe.name || authoredRecipe.code || "" }));
  }, [authoredRecipe?.product, authoredRecipe?.name, authoredRecipe?.code, dispatch, isRecord]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await source.load();
      if (mounted.current) setState(data);
    } catch {
      if (mounted.current) message.error(isRecord ? "Could not load this record." : "Could not load this operation type.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [source, message, isRecord]);

  useEffect(() => { load(); }, [load, treeRefresh]);
  // Runs after the tree's own refresh effect (a child's effects run first), so the selection
  // it clears on reload is put back to the operation that was just made.
  useEffect(() => {
    if (pendingSelect.current == null) return;
    dispatch(setOperationId(pendingSelect.current));
    pendingSelect.current = null;
  }, [treeRefresh, dispatch]);

  // The role master. Both role fields on a step pick from it — a typed role does not
  // resolve, and an approval rule matching on text eventually does not match.
  useEffect(() => {
    let alive = true;
    source.listChecklists?.().then((rows) => alive && setChecklists(rows)).catch(() => { });
    Promise.all([source.listParameters?.() || [], source.listLabels?.() || [], source.listUnits?.() || [], source.listControlCategories?.() || []])
      .then(([parameters, labels, units, categories]) => alive && setMasters({ parameters, labels, units, categories })).catch(() => { });
    source.listRules?.().then((rows) => alive && setRules(rows)).catch(() => { });
    source.listClearanceProfiles?.().then((rows) => alive && setClearanceProfiles(rows)).catch(() => { });
    return () => { alive = false; };
  }, [source]);

  useEffect(() => {
    let alive = true;
    source.listRoles?.()
      .then((rows) => alive && setRoles(
        rows.map((r) => ({ value: r.id, label: r.role_name }))
      ))
      .catch(() => { });
    return () => { alive = false; };
  }, [source]);

  /**
   * Every write carries the version we hold and one idempotency key per attempt. A 409 is
   * not a failure to show as one — someone else got there first, which is ordinary, and
   * their tree comes back with the refusal so the page rebases rather than asking again.
   */
  // The version we hold, read at call time: an Undo button created by an earlier write must
  // send the version the *latest* write returned, or the server rightly refuses it as stale.
  const stateRef = useRef(state);
  stateRef.current = state;
  // A record shows who performs each step. A type shows it only when its template carries
  // roles as defaults; a flat type has none and would say "no role" on every row.
  const showRole = typeof source.showRole === "function" ? source.showRole(state) : source.showRole !== false;
  const apply = useCallback(
    async (fn) => {
      if (!stateRef.current) return null;
      setBusy(true);
      const res = await fn({ version: stateRef.current.version, key: source.newKey() });
      if (!mounted.current) return null;
      setBusy(false);
      if (res.ok || res.conflict) {
        // 2026-10-07 · the new version is held at once, not on the next render: one Save can
        // send several writes in a row (an operation's name, then its order, …) and the second
        // read the version the first had already spent — the server answered "this record has
        // changed since you loaded it" to the author's own save.
        stateRef.current = { ...stateRef.current, ...res };
        setState((prev) => ({ ...prev, ...res }));
        if (res.conflict) {
          message.warning(
            "Someone else changed this record while you were editing. Your change was not " +
            "applied and the latest version is now shown."
          );
        }
        return res;
      }
      message.error(res.message);
      return res;
    },
    [message, source]
  );

  const recipe = state?.authored?.recipe || EMPTY_OBJECT;
  // The record's bill of materials. `Inspector` derives the same list for the side panel;
  // this is the drawer's copy, and both read the one reference the server sends.
  const bomLines = recipe?.references?.bom?.lines || EMPTY_LIST;
  const derived = state?.derived || EMPTY_OBJECT;
  const operations = useMemo(() => recipe.operations || EMPTY_LIST, [recipe]);
  // How many steps each operation holds, by its id: the tree's "Add Operation" waits on it.
  const stepCountByOperation = useMemo(() => Object.fromEntries(
    operations.map((o) => [String(o.id), (o.children || []).length])), [operations]);
  const findings = useMemo(() => state?.findings || EMPTY_LIST, [state]);
  const spans = useMemo(() => state?.authored?.spans || EMPTY_LIST, [state]);
  const span = useMemo(() => (selectedSpan === NEW_SPAN ? { id: NEW_SPAN }
    : spans.find((sp) => sp.id === selectedSpan) || null), [spans, selectedSpan]);
  // 2026-09-28 · no operation selected means none, not "the first one".
  //
  // This fell back to `operations[0]`, so opening a record — or picking a PROCESS on the
  // rail, which clears the operation — put the first operation's steps in the middle and
  // its facts in the inspector. The author had not asked for that operation and could not
  // tell it apart from one they had chosen; deselecting an operation appeared to do nothing
  // at all, because the fallback silently took its place.
  //
  // An operation TYPE is the exception: it is one pseudo-operation with no rail to pick it
  // from, so "none selected" would leave its page empty for good. It is its own subject.
  const op = useMemo(
    () => operations.find((o) => String(o.id) === String(selectedOperationId))
      || (!hasProcessRail ? operations[0] || null : null),
    [operations, selectedOperationId, hasProcessRail],
  );

  // The picker offers the kinds of step the operation type was configured with — the ones
  // chosen for it in operation-type configuration — rather than the whole vocabulary: on a
  // record operation built from the type (served as `type_ref.step_types`) and on the type's
  // own page (its `allowed_step_types`). Structures (a decision) stay. Nothing declared
  // offers everything, so an operation is never left with nothing to add.
  const opInserterSource = useMemo(() => {
    const allowed = source.kind === "type" ? state?.type?.allowed_step_types : op?.type_ref?.step_types;
    const steps = new Set((vocab.step_types || []).map((t) => t.value));
    const base = !allowed?.length ? INSERTER_SOURCE : INSERTER_SOURCE.filter((item) => !steps.has(item.value) || allowed.includes(item.value));
    // In-Process Check (step type IN_PROCESS_CHECK) · offered only on a 100 % visual inspection operation (its type code) whose
    // in-process sampling plan is configured, and only while it has no In-Process Check step. Every other picker leaves it out.
    const ipcOffered = op?.type_ref?.code === IPC_OPERATION_TYPE_CODE && !!op?.inspection?.ipc_sampling?.bands?.length && !op?.ipc_step?.node;
    const offered = ipcOffered ? base : base.filter((item) => item.value !== IPC_STEP_TYPE);
    // 2026-10-29 (owner) · and "Copy step": a step of any operation of this process, copied here (CopyStepDialog).
    return source.copyStep && op?.process_id
      ? [...offered, { value: COPY_STEP, label: "Copy step", family: "Copy", code: "CPY", hint: "From an operation of this process" }]
      : offered;
  }, [INSERTER_SOURCE, op, source, vocab, state?.type?.allowed_step_types]);
  // 2026-10-29 (owner) · where "Copy step" was picked ({rank, parent}), while its dialog is open
  const [copyAt, setCopyAt] = useState(null);
  const copyOperations = useMemo(
    () => operations.filter((o) => op && String(o.process_id) === String(op.process_id)), [operations, op]);
  const copyAllowed = useMemo(
    () => new Set(opInserterSource.map((item) => item.value).filter((v) => v !== COPY_STEP)), [opInserterSource]);

  // The step being edited in the right pane, as typed and not yet saved: laid over its row in
  // the sequence so the middle reads what the author is writing. The record changes on Save.
  const [stepDraft, setStepDraft] = useState(null);
  const onStepDraft = useCallback((code, patch) => {
    setStepDraft((cur) => (patch ? { code, patch } : (cur?.code === code ? null : cur)));
  }, []);
  const previewSteps = useMemo(() => {
    const steps = op?.children || EMPTY_LIST;
    if (!stepDraft) return steps;
    const p = stepDraft.patch;
    const roleName = (id) => roles.find((r) => String(r.value) === String(id))?.label;
    const shown = {};
    ["title", "instruction", "step_type", "stage"].forEach((k) => { if (k in p) shown[k] = p[k]; });
    if ("performed_by_id" in p) shown.role = p.performed_by_id ? (roleName(p.performed_by_id) || "") : "";
    if ("booth_ids" in p) {
      const machines = areaList.flatMap((a) => a.booths || []);
      shown.booths = (p.booth_ids || []).map((id) => {
        const m = machines.find((x) => String(x.id) === String(id));
        return { id, name: m?.name || "", code: m?.code || `#${id}` };
      });
    }
    if ("area_ids" in p) {
      shown.areas = (p.area_ids || []).map((id) => {
        const a = areaList.find((x) => String(x.id) === String(id));
        return { id, name: a?.name || "", code: a?.code || `#${id}` };
      });
    }
    const over = (nodes) => nodes.map((n) => (n.code === stepDraft.code
      ? { ...n, ...shown }
      : (n.children?.length ? { ...n, children: over(n.children) } : n)));
    return over(steps);
  }, [op, stepDraft, roles, areaList]);

  // Equipment carries forward. What an equipment step (setpoint / run) names is the equipment
  // the steps after it are worked on, until the next equipment step names other equipment —
  // the way a room carries forward from the step that states it. Read in the sequence's own
  // order, from what is on screen (so an unsaved pick carries at once). Display only: the
  // equipment is stored on the step that chose it, never copied onto the rest.
  const equipmentAt = useMemo(() => {
    const out = {};
    let carry = null;
    walk(previewSteps, (n) => {
      if (n.kind !== "STEP") return;
      const own = n.booths || [];
      if (own.length) {
        carry = { booths: own, from: n.position, fromTitle: n.title || "", fromCode: n.code };
        return;
      }
      if (carry) out[n.code] = carry;
    });
    return out;
  }, [previewSteps]);

  /** The selected process, and what it holds — derived from the operations it owns. */
  const processInfo = useMemo(() => {
    if (!selectedProcessId) return null;
    const own = operations.filter((o) => String(o.process_id) === String(selectedProcessId));
    if (!own.length) return null;
    let steps = 0;
    own.forEach((o) => walk(o.children, (n) => n.kind === "STEP" && (steps += 1)));
    return { id: selectedProcessId, name: own[0].process_name || "", operations: own, steps };
  }, [selectedProcessId, operations]);

  // 2026-10-14 · which steps of the operation in hand each continuous monitoring covers — from
  // the start of its From step to the end of its To step, operations between them whole. The
  // same rule the floor uses (span_window._covered), so the designer highlights what the
  // operator will see.
  const coveredBy = useMemo(() => {
    const out = {};
    if (!op || !spans.length) return out;
    const num = (id, code) => (operations.find((o) => o.id === id || o.code === code)?.number ?? null);
    const codes = [];
    walk(op.children || [], (n) => { if (n.kind === "STEP") codes.push(n.code); });
    spans.forEach((sp) => {
      const lo = num(sp.from_operation_id, sp.from?.operation), hi = num(sp.to_operation_id, sp.to?.operation);
      if (lo == null || hi == null || op.number < lo || op.number > hi) return;
      let a = 0, b = codes.length - 1;
      if (op.number === lo && codes.includes(sp.from_step)) a = codes.indexOf(sp.from_step);
      if (op.number === hi && codes.includes(sp.to_step)) b = codes.indexOf(sp.to_step);
      codes.slice(a, b + 1).forEach((c) => { (out[c] = out[c] || []).push({ id: sp.id, name: sp.name }); });
    });
    return out;
  }, [op, spans, operations]);

  const nodesByCode = useMemo(() => {
    const out = {};
    operations.forEach((o) => walk(o.children, (n) => (out[n.code] = n)));
    // 2026-10-14 · a continuous monitoring's parameters are steps too — opened in the same step
    // form as any parameter record, so they are configured exactly as that step type is.
    spans.forEach((sp) => walk(sp.children || [], (n) => (out[n.code] = n)));
    return out;
  }, [operations, spans]);
  const spanChildCodes = useMemo(() => {
    const out = new Set();
    spans.forEach((sp) => walk(sp.children || [], (n) => out.add(n.code)));
    return out;
  }, [spans]);

  // The step in hand belongs to the operation in hand. When the operation changes or is
  // deselected, a step that is not in it is let go — otherwise the right pane kept showing a
  // step of an operation no longer selected, instead of the process that is.
  useEffect(() => {
    if (!selected) return;
    let inOp = false;
    walk(op?.children || [], (n) => { if (n.code === selected) inOp = true; });
    if (!inOp) { setSelected(null); setStepOpen(false); }
  }, [op?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // A step may be added after another only once that one is saved and complete — a name and
  // where it is performed, with nothing unsaved in the form. Who performs it is not asked:
  // the operation's "Performed by" answers that, and a step's own role is optional.
  // Returns why not, or null. Loops and decisions are containers and never block.
  const canInsertAfter = useCallback((node) => {
    if (!node || node.kind !== "STEP") return null;
    const saved = nodesByCode[node.code] || node;
    const n = (saved.number ?? saved.position) ? `step ${saved.number ?? saved.position}` : "the step before";
    if (stepDraft?.code === node.code) return `Please save the previous step first — ${n} has changes that are not saved.`;
    if (!saved.title) return `Please save the previous step first — give ${n} a name.`;
    if (areaList.length && !(saved.areas || []).length) return `Please save the previous step first — choose where ${n} is performed ("May run in").`;
    return null;
  }, [nodesByCode, stepDraft, areaList]);

  const bySeverity = useMemo(() => {
    const out = { blocking: 0, advisory: 0 };
    findings.forEach((f) => {
      const n = findingCount(f);
      out[f.severity === "blocking" ? "blocking" : "advisory"] += n;
    });
    return out;
  }, [findings]);

  /**
   * The first blocking finding on a step — the finding itself, not its text. `Finding` reads
   * the check's own words; handing it a string would make the row the one place the surface
   * composed an explanation.
   *
   * Shown on the row because a finding an author has to go looking for is a finding they fix
   * last.
   */
  const findingFor = useCallback(
    (code) =>
      findings.find(
        (f) => f.severity === "blocking" && findingTouches(f, code)
      ),
    [findings]
  );

  const blockedCodes = useMemo(() => {
    const out = new Set();
    findings
      .filter((f) => f.severity === "blocking")
      .forEach((f) => findingNodes(f).forEach((c) => out.add(c)));
    return out;
  }, [findings]);

  const onEdit = (code, body) => apply((ctx) => source.editStep(code, body, ctx));
  /**
   * D13 · report after a direct manipulation, with a way back. The figures are the server's
   * (executions before and after, the new position) — never a client simulation — and Undo is
   * the inverse move, applied through the same write path.
   */
  const onMoveWithReport = async (step, body) => {
    const before = {
      position: step.position, parent: step.parent ?? null,
      executions: derived[`node:${step.code}#executions`]?.value
    };
    const res = await apply((ctx) => source.moveStep(step.code, body, ctx));
    if (!res?.ok) return res;
    let after = null;
    const find = (nodes, parent) => (nodes || []).forEach((n) => { if (n.code === step.code) after = { position: n.position, number: n.number, parent }; find(n.children, n.code); });
    (res.authored?.recipe?.operations || []).forEach((o) => find(o.children, null));
    const ex = res.derived?.[`node:${step.code}#executions`]?.value;
    const bits = [after ? `now step ${after.number ?? after.position}` : null,
    before.executions !== ex && ex != null ? `executions ${before.executions ?? "—"} → ${ex}` : null].filter(Boolean);
    const key = `move-${step.code}-${Date.now()}`;
    notification.open({
      key, message: `Moved ${step.code}${step.title ? ` · ${step.title}` : ""}`, description: bits.join(" · ") || undefined,
      duration: 9, placement: "bottom",
      btn: <Button size="small" onClick={() => { notification.destroy(key); apply((ctx) => source.moveStep(step.code, { rank: before.position, parent: before.parent }, ctx)); }}>Undo</Button>,
    });
    return res;
  };
  const onConcurrency = source.setConcurrency
    ? (operationId, stations) => apply((ctx) => source.setConcurrency(operationId, { stations }, ctx))
    : undefined;
  /** Insert at a point: a step of a type, or a decision with the vocabulary's default lanes. */
  // 2026-10-06 · a step is added to an operation whose details are filled and saved: its name
  // and the department that performs it. The server refuses it too (designer_view._placement).
  const opMissing = op && isRecord
    ? [!(op.name || "").trim() && "Operation name"].filter(Boolean)
    : [];
  const opMissingText = opMissing.length
    ? `Fill and save the operation's ${opMissing.join(" and ")} before adding steps.` : null;
  const insertAt = ({ rank, parent, stepType }) => {
    if (opMissingText) { antdMessage.warning(opMissingText); return undefined; }
    if (stepType === COPY_STEP) { setCopyAt({ rank, parent }); return undefined; }
    if (stepType === "DECISION") {
      return apply((ctx) => source.insertDecision({
        operation_id: op?.id, rank, parent,
        title: vocab.limits?.decision_title || "",
        lanes: vocab.limits?.decision_lanes,
      }, ctx)).then((r) => {
        // 2026-10-13 · opened at once, like a new repeat: what it depends on is the author's
        // next answer, and the inspector is where it is given.
        const code = r?.data?.created || r?.created;
        if (code) { setSelected(code); setStepOpen(true); }
        return r;
      });
    }
    if (stepType === "LOOP") {
      // An empty repeat, opened for its three questions. What it repeats over is left blank
      // deliberately: the author states it in the inspector, and a placeholder written here
      // would be this client deriving a fact the record has not been told yet.
      // The model refuses a LOOP that does not say what it repeats over, and a
      // collection-bounded one that states neither a count nor the fact behind it. Both are
      // right: a repeat that says neither is a repeat nobody can execute. So a new one is
      // inserted saying "not stated yet" in both, which is true, and the inspector opens on
      // it — the author answers there. The readiness check reports it until they do, so an
      // unanswered repeat cannot reach an issued batch.
      return apply((ctx) => source.insertStep({
        operation_id: op?.id, rank, parent, kind: "LOOP",
        bound_kind: "OVER_COLLECTION",
        over_subject: "not stated yet",
        bound_ref: "not stated yet",
      }, ctx)).then((r) => {
        const code = r?.data?.created || r?.created;
        if (code) { setSelected(code); setStepOpen(true); }
        return r;
      });
    }
    return apply((ctx) => source.insertStep({ operation_id: op?.id, rank, parent, step_type: stepType }, ctx));
  };
  const aqlSetup = source.saveAqlSetup && source.readAqlSetup ? { read: (code) => source.readAqlSetup(code), save: (code, setup) => apply((ctx) => source.saveAqlSetup(code, setup, ctx)) } : null;
  const onBind = source.bindRule ? (code, body) => apply((ctx) => source.bindRule(code, body, ctx)) : undefined;
  // Files and procedures on a step — only where the source has somewhere to put them.
  const files = source.uploadAttachment ? {
    onUpload: (code, file, meta) => apply((ctx) => source.uploadAttachment(code, file, meta, ctx)),
    onRemove: (code, id) => apply((ctx) => source.deleteAttachment(code, id, ctx)),
    onLinkSop: (code, body) => apply((ctx) => source.linkSop(code, body, ctx)),
    onUnlinkSop: (code, linkId, reason) => apply((ctx) => source.unlinkSop(code, linkId, { reason }, ctx)),
    searchSops: source.searchSops,
  } : null;
  // Inline authoring of the masters a step works with. Each write goes to the master, then
  // the record and the lists are read again so the row, the form and the picker agree.
  const refreshMasters = async () => {
    const [checklistRows, parameters] = await Promise.all([source.listChecklists?.() || [], source.listParameters?.() || []]);
    setChecklists(checklistRows); setMasters((m) => ({ ...m, parameters }));
    await load();
  };
  const checklistOps = source.setChecklistItems ? {
    // `current` is the list's own items as the editor shows them; the new one is appended to
    // THAT. Reading the step's saved items instead replaced a just-picked list's items with
    // the one being added.
    addItem: async (fieldId, name, current) => {
      const item = await source.createChecklistItem(name);
      const ids = [...(current || checklists.find((c) => String(c.id) === String(fieldId))?.item_ids || [])];
      if (item?.id) ids.push(item.id);
      await source.setChecklistItems(fieldId, ids);
      await refreshMasters();
    },
    setItems: async (fieldId, ids) => { await source.setChecklistItems(fieldId, ids); await refreshMasters(); },
    createList: async (name) => { const f = await source.createChecklist(name, []); await refreshMasters(); return f?.id; },
  } : null;
  const onCreateParameter = source.createParameter ? async (body) => {
    const res = await source.createParameter(body);
    await refreshMasters();
    return res?.data?.id ?? res?.id ?? res?.data?.data?.id;
  } : null;
  // RV9m · the typed fields a step collects — written as they are changed, like files.
  const fieldOps = source.addField ? {
    add: (code, body) => apply((ctx) => source.addField(code, body, ctx)),
    edit: (code, id, body) => apply((ctx) => source.editField(code, id, body, ctx)),
    remove: (code, id) => apply((ctx) => source.deleteField(code, id, ctx)),
    // A label not in the master yet: added there, put in the picker's list, and its id returned
    // so the field being written can use it straight away.
    createLabel: source.createLabel ? async (name) => {
      try {
        const res = await source.createLabel(name);
        const row = res?.data;
        if (!row?.id) { message.error(res?.message || "The label could not be added."); return null; }
        setMasters((m) => (m.labels.some((l) => l.id === row.id) ? m
          : { ...m, labels: [...m.labels, row].sort((a, b) => a.name.localeCompare(b.name)) }));
        message.success(res.message || `Label “${row.name}” added.`);
        return row.id;
      } catch (e) {
        message.error(e?.response?.data?.message || "The label could not be added.");
        return null;
      }
    } : null,
  } : null;
  const onTighten = source.tightenRule ? (code, id, limits) => apply((ctx) => source.tightenRule(code, id, limits, ctx)) : undefined;
  const onUnbind = source.unbindRule ? (code, id) => apply((ctx) => source.unbindRule(code, id, ctx)) : undefined;
  // RV9u · a timed rule updated from the plant policy it draws from.
  const onRefreshRule = source.refreshRuleFromPolicy ? (code, id) => apply((ctx) => source.refreshRuleFromPolicy(code, id, ctx)) : undefined;
  const onRefreshRules = source.refreshRulesFromPolicy ? () => apply((ctx) => source.refreshRulesFromPolicy(ctx)) : undefined;

  /**
   * Go to what a finding names. A queue that lists problems you then have to hunt for is a
   * report; going there is what makes it a work queue.
   */
  // Selecting a step leaves the span; the span's facet is for the span, not for a step in it.
  // A monitoring's own parameter is edited with the monitoring still open beside it.
  useEffect(() => { if (selected && !spanChildCodes.has(selected)) setSelectedSpan(null); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const onGoTo = useCallback(
    (where) => {
      if (!where) return;
      if (where.operation || where.operation_id) {
        const target = operations.find((o) => (where.operation && o.code === where.operation)
          || (where.operation_id != null && String(o.id) === String(where.operation_id)));
        if (target?.id) dispatch(setOperationId(target.id));
      }
      if (where.node) setSelected(where.node);
      // 2026-10-28 (owner) · going to a step shows the step — not the facet the link was pressed
      // on. Left on "blocking", a link to the step already selected changed nothing on screen.
      setFacet(where.node ? "step" : "details");
      setDag(false); setRightOpen(true);
      setPreflight(false);
    },
    [operations, dispatch]
  );
  // Picking another step leaves "what is blocking": that list was about the step it was opened on.
  useEffect(() => { setFacet((f) => (f === "blocking" ? "step" : f)); }, [selected]);

  const onDelete = async (code) => {
    let impact;
    try {
      impact = (await source.getImpact(code)).impact;
    } catch {
      message.error("Could not work out what deleting that would remove.");
      return;
    }
    modal.confirm({
      rootClassName: "dz",
      title: "Delete this step?",
      content: (
        <div>
          <p>
            {impact.steps} step{impact.steps === 1 ? "" : "s"} and {impact.bindings} rule
            {impact.bindings === 1 ? "" : "s"} would go.
          </p>
          {impact.payload_rows ? (
            <Alert
              type="warning"
              showIcon
              message={`What this step records or charges (${impact.payload_rows} row${impact.payload_rows === 1 ? "" : "s"}) is deleted with it.`}
            />
          ) : null}
          {(impact.cpv_designations || []).length ? (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 8 }}
              message={`CPV designation ${impact.cpv_designations.join(", ")} names this step — it is kept as history and cannot be deleted.`}
            />
          ) : null}
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true, disabled: !!(impact.cpv_designations || []).length },
      onOk: async () => {
        const res = await apply((ctx) =>
          source.deleteStep(code, { confirm_impact: impact.codes }, ctx)
        );
        if (res?.ok) setSelected(null);
      },
    });
  };

  const onAdd = (stepType) => insertAt({ stepType });

  // The spinner is for the first load only. A reload after a write (a checklist item added,
  // the tree refreshed) used to swap the whole designer for it and back — which read as the
  // page refreshing, and threw away the open form, its unsaved draft and the open sections.
  // Once there is a record on screen, it stays while the fresh one is fetched.
  if (loading && !state) {
    return <Flex justify="center" style={{ padding: 48 }}><Spin /></Flex>;
  }
  if (!state) return <Empty description={isRecord ? "This record could not be loaded." : "This operation type could not be loaded."} />;

  const blocking = findings.filter((f) => f.severity === "blocking").length;
  const advisoryCount = findings.filter((f) => f.severity !== "blocking").length;
  let opSteps = 0;
  walk(op?.children, (n) => n.kind === "STEP" && (opSteps += 1));

  const rail = (
    <InspectorRail
        active={dag ? "dag" : inspFacets.at}
        onChange={(k) => {
          if (k === "back") return navigate(source.backTo);
          if (k === "dag") return setDag((v) => !v);
          if (k === "paper") return navigate(`/record-designer/${recipeId}/view`);
          if (k === "preflight") return setPreflight(true);
          if (k === "left") return setLeftOpen((v) => !v);
          if (k === "wide") return setWide((v) => !v);
          if (k === "fields") return setFieldsOpen(true);
          if (k === "right") return setRightOpen((v) => !v);
          setDag(false); setWide(false); setRightOpen(true); setFacet(k);
        }}
        items={[
          // The way out, first — the mock's rail opens with an arrow. Records, not history:
          // a rail that took you "back" to wherever you came from would not be a rail.
          { key: "back", icon: ArrowLeft, label: source.backLabel, tone: "plain" },
          ...inspFacets.items,
          isRecord ? {
            key: "dag", icon: GitBranch, label: dag ? "Back to the steps" : "The record as a DAG",
            badge: dag ? true : null, tone: "plain"
          } : null,
          // The same record laid out as the paper BMR it becomes — read-only, printable.
          isRecord ? { key: "paper", icon: FileText, label: "View as paper", tone: "plain" } : null,
          // One control, one fact: the record's readiness, with the count; pressing it opens
          // the list of what is in the way.
          {
            key: "preflight", icon: Rocket,
            label: blocking ? `Not yet · ${blocking} blocking — open pre-flight`
              : advisoryCount ? `Ready · ${advisoryCount} advisory — open pre-flight`
                : "Ready · nothing in the way — open pre-flight",
            badge: blocking || advisoryCount || null,
            tone: blocking ? "block" : advisoryCount ? "warn" : "plain"
          },
          { key: "fields", icon: SlidersHorizontal, label: "Show / hide fields", tone: "plain" },
          {
            key: "wide", icon: wide ? Minimize2 : Maximize2,
            label: wide ? "Show all panels" : "Full view of the sequence",
            badge: wide ? true : null, tone: "plain"
          },
        ].filter(Boolean)}
      />
  );

  const body = (
    <div className="dz">
      {/* D · the page names the record. The old header said nothing about which operation
          you were editing, so two open tabs were indistinguishable (U-Q2). */}
      {/* No header row. Everything it held — readiness and pre-flight, the layout toggles,
          the record line — lives on the rail or in the app header, and the sequence has the
          height. The rail is fixed to the viewport, so it is here rather than in a column. */}
      {/* 2026-10-28 (owner) · the rail floats on a record too — vertical, on the right, movable
          by its grip — and carries "View as DAG" and "View as paper". */}
      {rail}
      <FieldsDialog open={fieldsOpen} onClose={() => setFieldsOpen(false)} />

      {isRecord ? <RecordStatus finalized={finalized} /> : null}
      {isRecord ? <LifecycleProbe onLocked={setLifecycleLocked} /> : null}
      {isRecord && source.lock ? (
        <LockBanner status={editLock.status} lock={source.lock}
          onTakenOver={async (st) => { onLockChange(st); await editLock.refresh(); await load(); }} />
      ) : null}
      <div ref={frameRef} className={`dz-frame ${!hasProcessRail ? "no-left" : leftOpen ? "" : "left-closed"} ${wide ? "wide" : ""} ${rightOpen ? "" : "right-closed"}`}>
        {/* 2026-10-06 · the panel is HIDDEN when closed, not unmounted. Unmounting threw away
            the tree's state, and remounting ran ProcessHierarchy's mount effect, which clears
            the selected operation — so ‹ then › left the centre empty ("blank"). Kept mounted,
            the open process, the expanded rows and the selection survive a collapse.
            The panel hides ITSELF (it stays the frame's direct child): wrapped in a div, the
            column rules `.dz-frame > .dz-col` stopped matching it and it lost its full height. */}
        {!hasProcessRail ? null : (
          <ProcessPanel hidden={!leftOpen} recipeId={recipeId} onLeftOpen={setLeftOpen} spans={spans} operations={operations}
            stepCounts={stepCountByOperation}
            addMiddle={addMiddle} setAddMiddle={setAddMiddle} readOnly={readOnly}
            onAddCustom={source.addOperation && !readOnly ? async ({ operation_type_id, standard_block_type_id, copy_from_operation_id, after }) => {
              if (!selectedProcessId) { message.error("Pick the process to add the operation to."); return; }
              const res = await apply((ctx) => source.addOperation({ process_id: selectedProcessId, after,
                ...(copy_from_operation_id ? { copy_from_operation_id }
                  : standard_block_type_id ? { standard_block_type_id } : { operation_type_id }) }, ctx));
              if (!res?.ok || !res.created_operation) return;
              pendingSelect.current = res.created_operation;
              setFocusOperation(res.created_operation);
              setSelected(null); setSelectedSpan(null);
              setRightOpen(true); setFacet("details");
              dispatch(toggleRefresh());
            } : undefined}
            selectedSpan={selectedSpan} onSelectSpan={(id) => { setSelectedSpan(id); setRightOpen(true); }}
            onSpansChanged={isRecord ? load : undefined}
            onAddSpan={isRecord && !readOnly ? () => {
              // The form opens in the right panel. The selected step lets go; the operation stays
              // selected (owner, 2026-10-14), so the middle panel keeps showing its steps.
              setSelected(null); setStepOpen(false);
              setSelectedSpan(NEW_SPAN); setFacet("span"); setRightOpen(true);
            } : undefined} />
        )}
        {hasProcessRail && !leftOpen ? (
          <button className="dz-strip" onClick={() => setLeftOpen(true)} aria-label="Show processes">
            <PanelLeftOpen size={15} />
            <span>Processes</span>
          </button>
        ) : null}

        <div className="dz-col">
          {/* U0 · the SAME component the library renders a type's sub-operations from. */}
          {/* 2026-10-28 (owner) · no "n continuous across this operation" strip above the steps:
              the author already knows a step is continuous — each one carries its SPN chip — and a
              continuous operation opens from the process panel's list on the left. */}
          {dag ? (
            /* 2026-10-12 · the record as a DAG, full screen: a graph of every operation needs the
               width of the page, not the middle pane between the tree and the panel. A layer of
               its own above the page (portalled to <body> so no parent clips it), closed with
               the button or Esc. */
            <DagOverlay title={recipe?.name || "The record"} onClose={() => setDag(false)}>
              <ArchiMateFlow
                data={{ recipe_id: Number(recipeId) }}
                set_flow_data={(v) => { if (!v) setDag(false); }}
                isClickable={false}
                height="calc(100vh - 52px)"
              />
            </DagOverlay>
          ) : null}
          {dag ? null : !op ? (
            /* 2026-09-28 · no operation, no sequence. The middle pane belongs to the
               operation in hand; with none chosen it used to show the first operation's
               steps, which is a record the author never asked to see. */
            <div className="dz-empty">
              {selectedProcessId && !processInfo && hasProcessRail ? (
                /* A process with no operations yet: nothing to pick on the left, so the
                   way forward is here — the same chooser the tree's Add Operation opens. */
                <Flex vertical gap={10} align="center">
                  <Text type="secondary">
                    {`${selectedProcessName || "This process"} has no operations yet.`}
                  </Text>
                  {!readOnly ? (
                    <Button type="primary" icon={<Plus size={14} />} onClick={() => setAddMiddle(true)}>
                      Add operation
                    </Button>
                  ) : null}
                </Flex>
              ) : (
                <Flex vertical align="center" gap={10}>
                  <Text type="secondary">
                    {processInfo
                      ? `${processInfo.name || "This process"} — pick an operation on the left to see its steps.`
                      : "Pick a process on the left, then an operation, to see its steps."}
                  </Text>
                  {/* With the panel collapsed "on the left" is a thin strip; say where to go. */}
                  {hasProcessRail && !leftOpen ? (
                    <Button icon={<PanelLeftOpen size={14} />} onClick={() => setLeftOpen(true)}>Show processes</Button>
                  ) : null}
                </Flex>
              )}
            </div>
          ) : (
            <SequenceView
              vocab={vocab}
              steps={previewSteps}
              coveredBy={coveredBy}
              onOpenSpan={(id) => { setSelectedSpan(id); setRightOpen(true); }}
              equipmentAt={equipmentAt}
              canInsertAfter={readOnly ? undefined : canInsertAfter}
              insertSource={opInserterSource}
              concurrency={op?.concurrency}
              showRole={showRole}
              onInsert={readOnly ? undefined : insertAt}
              onWrap={source.wrapInLoop && !readOnly ? ({ codes, over, ref }) =>
                apply((ctx) => source.wrapInLoop(op?.id, { children: codes, over, ref, kind: "OVER_COLLECTION" }, ctx)) : undefined}
              onOpen={(code) => { setSelected(code); setStepOpen(true); }}
              view={view}
              onViewChange={setView}
              selected={selected}
              onSelect={setSelected}
              blockedCodes={blockedCodes}
              findingFor={findingFor}
              actions={!readOnly}
              onMove={readOnly ? undefined : (step, delta) => {
                // One place up or down means the NEIGHBOUR's rank: stored ranks can have gaps
                // (a deleted step leaves one), and "position ± 1" then named no step — the
                // server read it as a list index and the step jumped the wrong way.
                const siblingsOf = (nodes) => {
                  for (const n of nodes || []) {
                    if (n.code === step.code) return nodes;
                    const found = siblingsOf(n.children);
                    if (found) return found;
                  }
                  return null;
                };
                const sibs = (siblingsOf(op?.children) || []).filter((n) => n.kind !== "HEADING");
                const i = sibs.findIndex((n) => n.code === step.code);
                const target = i >= 0 ? sibs[i + delta] : null;
                if (!target) return;
                onMoveWithReport(step, { rank: target.position });
              }}
              designMode={designMode}
              onRemove={readOnly ? undefined : (step) => onDelete(step.code)}
              emptyNote={
                op?.unplaced ? (
                  /* Not empty — **unplaced**. The operation holds rows nobody has ever put in
                     an order, and saying "no steps yet" over content is the tidier lie. */
                  <Flex vertical gap={10} align="center" style={{ maxWidth: 460 }}>
                    <Text strong>
                      {op.unplaced} row{op.unplaced === 1 ? "" : "s"} here have never been put
                      in an order.
                    </Text>
                    <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))", textAlign: "center" }}>
                      They were authored before the record stored the order an operator works
                      in. Nothing will guess it — insertion order is a plausible guess, not an
                      order anyone chose — so a person accepts one.
                    </Text>
                    <Button size="small" type="primary" onClick={() => setPlacing(op)}>
                      Put them in order
                    </Button>
                  </Flex>
                ) : (
                  "No steps yet. An empty operation is a blank the operator cannot fill."
                )
              }
              toolbar={
                <Space size={10}>
                  <span className="dz-eyebrow">{op?.name || "—"}</span>
                  {/* 2026-10-28 (owner) · no cleanroom grade here ("Grade C (ISO 8)"): where the work runs is
                      each step's "May run in". Inside the aseptic boundary is still said — it is not a grade. */}
                  {op?.aseptic ? <span className="dz-chip par" title="Inside the aseptic boundary">Aseptic</span> : null}
                  <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
                    {opSteps} step{opSteps === 1 ? "" : "s"}
                  </Text>
                  {/* Pick from the closed vocabulary, never type — a typed step type is one
                      the engine cannot evaluate. Only for an operation with nothing in it:
                      once there are steps, a step is added where it goes, between two others. */}
                  {source.startReadOnly && !source.viewOnly ? (
                    /* View is the default for master data; Edit is the deliberate step. */
                    <Segmented size="small" value={editing ? "Edit" : "View"} options={["View", "Edit"]}
                      onChange={(v) => setEditing(v === "Edit")} />
                  ) : null}
                  {op && !opSteps && !readOnly ? (
                    <Tooltip title={opMissingText}>
                      <span>
                        <Inserter
                          source={opInserterSource}
                          label="Add step"
                          disabled={busy || readOnly || !!opMissingText}
                          onPick={(item) => onAdd(item.value)}
                        />
                      </span>
                    </Tooltip>
                  ) : null}
                </Space>
              }
            />
          )}
        </div>

        <CopyStepDialog
          open={!!copyAt}
          onClose={() => setCopyAt(null)}
          operations={copyOperations}
          current={op}
          allowed={copyAllowed}
          typeLabel={(t) => LABEL[t]}
          onCopy={async (code) => {
            const res = await apply((ctx) => source.copyStep(code, { operation_id: op?.id, rank: copyAt?.rank, parent: copyAt?.parent }, ctx));
            const created = res?.data?.created || res?.created;
            if (created) { setSelected(created); setStepOpen(true); antdMessage.success(`${code} copied as ${created}`); }
            return !!res?.ok;
          }}
        />

        <Drawer
          rootClassName="dz"
          title={placing ? `Put ${placing.name} in order` : ""}
          open={!!placing}
          onClose={() => setPlacing(null)}
          width={640}
          styles={{ body: { padding: 20 } }}
        >
          {proposal ? (
            <Flex vertical gap={14}>
              <Alert
                type="warning"
                showIcon
                message={`Proposed from ${proposal.basis}.`}
                description="Accepting writes an order nobody authored, so the record will say who decided and why."
              />
              <Flex vertical gap={4}>
                {proposal.rows.map((r) => (
                  <Flex key={`${r.kind}-${r.id}`} gap={10} align="baseline"
                    className="dz-kv" style={{ padding: "4px 0" }}>
                    <span className="dz-code">{r.proposed_rank}</span>
                    <span style={{ flex: 1 }}>{r.label}</span>
                    <span className={r.resolved ? "dz-kind" : ""}
                      style={r.resolved ? {} : { color: "var(--dz-block)" }}>
                      {r.step_type || "no step type — a person must decide"}
                    </span>
                  </Flex>
                ))}
              </Flex>
              <Input.TextArea
                autoSize={{ minRows: 2 }}
                placeholder="Why this order — the record keeps it"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
              />
              <Flex gap={8} justify="flex-end">
                <Button onClick={() => setPlacing(null)}>Cancel</Button>
                <Button
                  type="primary"
                  disabled={!why.trim() || busy}
                  onClick={async () => {
                    const res = await apply((ctx) =>
                      source.placeRows(placing.id, { why }, ctx)
                    );
                    if (res?.ok) setPlacing(null);
                  }}
                >
                  Accept this order
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex justify="center" style={{ padding: 32 }}><Spin /></Flex>
          )}
        </Drawer>

        <Drawer
          rootClassName="dz dz-stepdrawer"
          title={selected ? `Step · ${selected}` : "Step"}
          open={stepOpen && !!selected}
          onClose={() => setStepOpen(false)}
          width={720}
          // Fitted between the app header (64px) and the fixed footer, so neither covers it.
          rootStyle={{ top: 64, bottom: 28 }}
          styles={{ body: { padding: 20 } }}
        >
          {selected && nodesByCode[selected] ? (
            <StepForm
              recordBadge={recordBadgeOf(recipe)}
              step={{
                ...nodesByCode[selected],
                step_type_label: LABEL[nodesByCode[selected].step_type],
              }}
              readOnly={readOnly}
              masters={masters}
              fieldOps={fieldOps}
              checklistOps={checklistOps}
              onCreateParameter={onCreateParameter}
              stepTypes={STEP_TYPE_OPTIONS}
              // 2026-10-29 (owner) · the type dropdown offers what the add-step picker offers: the types the
              // operation type allows (a record's `type_ref.step_types`, a type page's `allowed_step_types`)
              allowedStepTypes={source.kind === "type" ? state?.type?.allowed_step_types : op?.type_ref?.step_types}
              signMeanings={SIGN_MEANINGS}
              signLabels={SIGN_LABELS}
              roles={roles}
              showRole={showRole}
              blastRadius={source.blastRadius?.(state) || null}
              rules={rules}
              aqlSetup={aqlSetup}
              onBind={onBind ? (body) => onBind(selected, body) : undefined}
              onTighten={onTighten ? (id, limits) => onTighten(selected, id, limits) : undefined}
              onUnbind={onUnbind ? (id) => onUnbind(selected, id) : undefined}
              onRefresh={onRefreshRule ? (id) => onRefreshRule(selected, id) : undefined}
              vocab={vocab}
              checklists={checklists}
              clearanceProfiles={clearanceProfiles}
              operation={op}
              derived={derived}
              // 2026-10-13 · inside a repeat, as the right pane already says: the material comes from
              // the collection, one per pass. Without it a charge step in a loop opened here demanded
              // "Choose the material this step moves before saving" — a material the loop supplies.
              inLoop={!!derived[`node:${selected}#membership`]?.value}
              // The record's bill of materials — what a charge step may charge, and what a
              // repeat over materials runs over. The other StepForm already passed it; this
              // one did not, so the loop inspector opened here showed an empty bill on a
              // record that plainly had one.
              bomLines={bomLines}
              laneRoles={op?.dispensing?.roles || []}
              nodesByCode={nodesByCode}
              onEditNode={onEdit}
              onConcurrency={onConcurrency ? (n) => onConcurrency(op?.id, n) : undefined}
              onChange={(patch) => onEdit(selected, patch)}
              onDraft={onStepDraft}
              carriedEquipment={(selected && equipmentAt[selected]) || null}
              areaList={areaList}
              files={files}
              cqas={recipe.references?.quality?.cqas || []}
            />
          ) : null}
        </Drawer>

        <Drawer
          rootClassName="dz"
          title={null}
          open={preflight}
          onClose={() => setPreflight(false)}
          width={520}
          styles={{ body: { padding: 16 } }}
        >
          {/* D12 · the third placement. Same component, same words — a fix the record can
              perform is offered here, and going to a finding lands on the thing it names. */}
          <PreflightAll
            findings={findings}
            onGoTo={onGoTo}
            title={`${recipe.code || "Record"} · pre-flight`}
          />
        </Drawer>

        {wide || !rightOpen ? null : (
          <Inspector
            processInfo={processInfo}
            onGoTo={onGoTo}
            roles={roles}
            operation={op}
            node={selected ? nodesByCode[selected] : null}
            findings={findings}
            counts={bySeverity}
            spans={state.authored?.spans || []}
            recipe={recipe}
            version={state.version}
            busy={busy}
            onEdit={onEdit}
            onDelete={onDelete}
            dag={dag}
            facet={facet}
            onFacets={onFacets}
            onPickFacet={(k) => { setDag(false); setWide(false); setRightOpen(true); setFacet(k); }}
            span={span}
            onClearSpan={() => setSelectedSpan(null)}
            spanPanel={isRecord ? {
              recipeId, operations, masters, readOnly,
              onSaved: async (code) => { await load(); if (code) setSelectedSpan(code); },
              onReload: load,
              onClose: () => setSelectedSpan(null),
              onInsertParam: async (code, { parameter_id = null, title = "Parameter", open = true } = {}) => {
                const res = await apply((ctx) => source.insertStep(
                  { span: code, kind: "STEP", step_type: "PARAMETER_RECORD", title,
                    ...(parameter_id ? { parameter_id } : {}) }, ctx));
                // Straight into its step form, the parameter record's own.
                if (open && res?.ok && res.created) { setSelected(res.created); setStepOpen(true); }
                return res;
              },
              onOpenParam: (code) => { setSelected(code); setStepOpen(true); },
              // A hold time between operations (span_clock): its one HOLD step, and its CLOCK limit.
              onInsertHold: (code, { title = "Hold time" } = {}) => apply((ctx) => source.insertStep(
                { span: code, kind: "STEP", step_type: "HOLD", title }, ctx)),
              onBindRule: source.bindRule ? (code, body) => apply((ctx) => source.bindRule(code, body, ctx)) : undefined,
              onUnbindRule: source.unbindRule ? (code, id) => apply((ctx) => source.unbindRule(code, id, ctx)) : undefined,
              onEditStep: (code, body) => apply((ctx) => source.editStep(code, body, ctx)),
              onDeleteStep: async (code) => {
                const impact = (await source.getImpact(code)).impact;
                return apply((ctx) => source.deleteStep(code, { confirm_impact: impact.codes }, ctx));
              },
            } : null}
            vocab={vocab}
            STEP_TYPE_OPTIONS={STEP_TYPE_OPTIONS}
            SIGN_MEANINGS={SIGN_MEANINGS}
            SIGN_LABELS={SIGN_LABELS}
            LABEL={LABEL}
            checklists={checklists}
            rules={rules}
            aqlSetup={aqlSetup}
            onBind={onBind}
            onTighten={onTighten}
            onUnbind={onUnbind}
            onRefresh={onRefreshRule}
            derived={derived}
            nodesByCode={nodesByCode}
            onConcurrency={onConcurrency}
            isRecord={isRecord}
            showRole={showRole}
            blastRadius={source.blastRadius?.(state) || null}
            extraDetails={source.renderDetails?.(state, { navigate, reload: load, busy, readOnly }) || null}
            onOpenMfr={source.openMfr ? (m) => source.openMfr(navigate, m) : undefined}
            onBomFromMfr={source.bomFromMfr ? (body) => apply((ctx) => source.bomFromMfr(body, ctx)) : undefined}
            onRefreshRules={onRefreshRules}
            onHandover={source.setHandover ? (operationId, body) => apply((ctx) => source.setHandover(operationId, body, ctx)) : undefined}
            onReconciliation={source.setReconciliation ? (operationId, body) => apply((ctx) => source.setReconciliation(operationId, body, ctx)) : undefined}
            onLotStep={source.setLotStep ? (operationId, body) => apply((ctx) => source.setLotStep(operationId, body, ctx)) : undefined}
            onDependsOn={source.setDependsOn ? (operationId, body) => apply((ctx) => source.setDependsOn(operationId, body, ctx)) : undefined}
            onArea={source.setArea ? (operationId, grade) => apply((ctx) => source.setArea(operationId, { grade }, ctx)) : undefined}
            onDispensing={source.setDispensing
              ? (operationId, body) => apply((ctx) => source.setDispensing(operationId, body, ctx)) : undefined}
            areaList={areaList}
            onPromote={source.promote ? (operation) => setPromoting(operation) : undefined}
            onUpdateFromType={source.updateFromType ? (operation) => setUpdating(operation) : undefined}
            onEditOperation={source.editOperation ? (operationId, body) => apply((ctx) => source.editOperation(operationId, body, ctx))
              .then((res) => {
                // The tree reloads to show the new name; keep this operation selected through it.
                if (res?.ok) { pendingSelect.current = operationId; dispatch(toggleRefresh()); }
                return res;
              }) : null}
            departments={departments}
            onNameDraft={(name) => dispatch(setDraftOperationName(name))}
            focusOperation={focusOperation}
            onStepDraft={onStepDraft}
            equipmentAt={equipmentAt}
            clearanceProfiles={clearanceProfiles}
            readOnly={readOnly}
            masters={masters}
            fieldOps={fieldOps}
            checklistOps={checklistOps}
            onCreateParameter={onCreateParameter}
            files={files}
          />
        )}
        {updating ? (
          <UpdateFromTypeModal
            operation={updating}
            busy={busy}
            preview={() => source.typeUpdatePreview(updating.id)}
            onCancel={() => setUpdating(null)}
            onOk={async (force) => {
              const res = await apply((ctx) => source.updateFromType(updating.id, { force }, ctx));
              if (res) {
                setUpdating(null);
                notification.open({
                  message: `Updated to v${updating.type_ref?.version}`,
                  description: `${updating.name} was re-authored from the type's current version.`, duration: 6
                });
              }
            }}
          />
        ) : null}
        {promoting ? (
          <PromoteModal
            operation={promoting}
            busy={busy}
            onCancel={() => setPromoting(null)}
            onOk={async (body) => {
              const res = await apply((ctx) => source.promote(promoting.id, body, ctx));
              if (res?.type) {
                setPromoting(null);
                notification.open({
                  message: `Saved as operation type ${res.type.code}`,
                  description: (
                    <span>{res.type.name} · version {res.type.version}. This operation is now built from it.{" "}
                      <a onClick={() => navigate(`/operation-type/${res.type.id}`)}>Open it in the library</a></span>
                  ),
                  duration: 8,
                });
              }
            }}
          />
        ) : null}
      </div>
    </div>
  );
  // The readiness roll-up is a record's. A type mounts the same body without the provider.
  return isRecord ? (
    <ReadinessProvider recipeId={recipeId}
      onNavigateToOperation={(id) => dispatch(setOperationId(id))}>
      {body}
    </ReadinessProvider>
  ) : body;
}
